/**
 * AIWorkflows API Service
 *
 * Typed API client for all AIWorkflows endpoints.
 * Uses the AIWorkflows microservice URL (default: /aiworkflows)
 */

import { config } from '../../config'
import type { OpenAPISpec } from '../../components/OpenApiRenderer/types/openapi'
import { formatCreatedAtInMetricsExportCsv } from '../../features/workflows/utils/workflowMetricsExportCsv'
import { ApiError, extractErrorMessage, getAssumedRoleIdForRequest, isTokenExpiringSoon, refreshAccessToken } from './core'
import { maybeCaptureApiError } from '../../features/bug-report/captureApiError'
import { createServiceFetch } from '../../utils/validateFetchUrl'
import type {
  WorkflowNode,
  WorkflowEdge,
  WorkflowNodeGroup,
  WorkflowCanvasAnnotation,
  WorkflowStatus,
} from '../../features/workflows/types'

// =============================================================================
// TYPES
// =============================================================================

export interface WorkflowCreatePayload {
  name: string
  description?: string
  project_id: string
  graph_definition?: { nodes: WorkflowNode[]; edges: WorkflowEdge[]; node_groups?: WorkflowNodeGroup[]; canvas_annotations?: WorkflowCanvasAnnotation[] }
  trigger_config?: WorkflowTriggerConfig | null
  execution_config?: Record<string, unknown>
  tags?: string[] | null
  icon_file_id?: string | null
}

export interface WorkflowScheduleConfig {
  scheduleType?: 'cron' | 'interval'
  cronExpression?: string
  intervalSeconds?: number
  timezone?: string
}

export interface WorkflowTriggerConfig extends WorkflowScheduleConfig {
  /** Explicit trigger type (manual, schedule, webhook, storage, database, email, user_input) — legacy single-trigger */
  type?: 'manual' | 'schedule' | 'webhook' | 'storage' | 'database' | 'email' | 'user_input'
  /** Active auto-triggers (schedule, webhook, storage, database, email, user_input may be combined) */
  enabledTriggers?: Array<'manual' | 'schedule' | 'webhook' | 'storage' | 'database' | 'email' | 'user_input'>
  schedule?: WorkflowScheduleConfig
  webhook?: WebhookTriggerConfig
  storage?: StorageTriggerConfig
  database?: DatabaseTriggerConfig
  email?: EmailTriggerConfig
  /** Manual-run UX: prompt text bound to input_data.user_prompt / text (not a poller) */
  userInput?: UserInputTriggerConfig
}

export interface UserInputTriggerConfig {
  enabled?: boolean
  /** Prompt text used as root input_data.user_prompt and input_data.text on Run */
  prompt?: string
}

export interface WebhookTriggerConfig {
  enabled?: boolean
  inputMapping?: 'passthrough' | 'jsonpath'
  jsonpath?: Record<string, string>
  maxPayloadKb?: number
  rateLimitRpm?: number
}

export interface StorageTriggerConfig {
  enabled?: boolean
  registeredFolderId: string
  filePattern?: string
  recursive?: boolean
  events?: Array<'created' | 'modified'>
  pollIntervalSeconds?: number
  minAgeSeconds?: number
  batchSize?: number
  maxEventsPerRun?: number
  debounceWindowSeconds?: number
}

export interface DatabaseTriggerConfig {
  enabled?: boolean
  connectionId: string
  registeredTableId: string
  markerColumn: string
  events?: Array<'inserted' | 'updated'>
  pollIntervalSeconds?: number
  batchSize?: number
  maxEventsPerRun?: number
  debounceWindowSeconds?: number
  customFilter?: string
}

export interface EmailTriggerConfig {
  enabled?: boolean
  authConnectionId?: string
  iamId?: string
  connectionId?: string
  folder?: string
  searchQuery?: string
  pollIntervalSeconds?: number
  maxEventsPerRun?: number
  debounceWindowSeconds?: number
}

export interface WorkflowGenerateDescriptionPayload {
  graph_definition?: {
    nodes: WorkflowNode[]
    edges: WorkflowEdge[]
  }
}

export interface WorkflowGenerateDescriptionResponse {
  description: string
  source: 'ai' | 'heuristic'
}

export interface ReversePromptDialsPayload {
  topological_rigidity: number
  code_script_fidelity: number
  resource_binding_tightness: number
}

export interface WorkflowReversePromptPayload {
  graph_definition?: {
    nodes: WorkflowNode[]
    edges: WorkflowEdge[]
  } | Record<string, unknown>
  originality_percentage?: number
  dials?: ReversePromptDialsPayload
  target_persona?: 'developer' | 'business_user'
  options?: {
    enable_self_play?: boolean
    max_self_play_iters?: number
    enable_pruning?: boolean
    prune_node_threshold?: number
  }
  project_context?: Record<string, unknown>
}

export interface WorkflowReversePromptResponse {
  prompt: string
  sections: Record<string, string>
  gaps: Array<{
    node_id: string
    field: string
    message: string
    infusion_rule: string
    remapped_to?: string | null
  }>
  code_contracts: Array<{
    node_id: string
    imports: string[]
    functions: string[]
    free_vars: string[]
    output_keys: string[]
    parse_error?: string | null
  }>
  verification?: {
    score: number
    iterations: number
    passed: boolean
    detail?: string | null
  } | null
  dials_applied: ReversePromptDialsPayload
  persona_applied?: 'developer' | 'business_user'
  source: 'ai' | 'heuristic'
  pruned?: boolean
  super_nodes?: Array<Record<string, unknown>>
}

export interface WorkflowUpdatePayload {
  name?: string
  description?: string
  graph_definition?: {
    nodes: WorkflowNode[]
    edges: WorkflowEdge[]
    node_groups?: WorkflowNodeGroup[]
    canvas_annotations?: WorkflowCanvasAnnotation[]
  }
  trigger_config?: WorkflowTriggerConfig | null
  execution_config?: Record<string, unknown>
  /** Merged into execution_config.privacy on the server (PII/PHI masking). */
  privacy?: WorkflowPrivacyConfig
  /** Merged into execution_config.claim_check on the server (post-run blob retention). */
  claim_check?: { retention_seconds: number }
  /** Merged into execution_config.upload_storage. Empty folder keeps current storage. */
  upload_storage?: { folder_id: string | null }
  tags?: string[] | null
  icon_file_id?: string | null
  /** Registry catalog metadata (custom fields defined under Platform Admin → Configurations). */
  catalog_metadata?: Record<string, unknown> | null
  /** Optimistic locking — send the lock_version received from GET to detect concurrent edits */
  lock_version?: number
}

/** Backend-curated field docs for workflow node configuration (AIWorkflows). */
export interface WorkflowNodeConfigFieldDoc {
  key: string
  label: string
  section: string
  description_markdown: string
}

export interface WorkflowNodeConfigReferenceResponse {
  schema_version: number
  node_type: string
  title: string
  intro_markdown: string
  has_detailed_field_docs: boolean
  fields: WorkflowNodeConfigFieldDoc[]
}

export interface WorkflowNodeConfigReferenceIndexResponse {
  schema_version: number
  node_types: string[]
}

/** Single config parameter in a node's schema. */
export interface NodeConfigParam {
  name: string
  type: string
  required: boolean
  description: string
  default?: unknown
}

/**
 * Declared JSON-Schema contract for one side of a node (input or output).
 *
 * Backend-declared, so it is available before the workflow has ever run —
 * unlike the run data the canvas Input/Output Schema tabs used to need.
 */
export interface NodeDeclaredSchema {
  type?: string
  description?: string
  required?: string[]
  properties?: Record<string, NodeDeclaredSchema>
  items?: NodeDeclaredSchema
}

/** Schema entry for one node type: IO contracts + config parameters. */
export interface NodeSchemaEntry {
  name: string
  category: string
  description: string
  is_beta: boolean
  inputs: string[]
  outputs: string[]
  config_parameters: NodeConfigParam[]
  /** Declared input contract; `inputs` is the same thing flattened to labels. */
  input_schema?: NodeDeclaredSchema | null
  /** Declared output contract; `outputs` is the same thing flattened to labels. */
  output_schema?: NodeDeclaredSchema | null
}

export interface OcrAnalyzerTemplate {
  id: string
  family: string
  group: 'foundation' | 'domain' | string
  label: string
  description: string
  capabilities: string[]
  document_types: string[]
  extraction_scope: string
  instructions: string
  schema: Record<string, unknown>
  type_schemas: Record<string, Record<string, unknown>>
}

export interface OcrAnalyzerTemplatesResponse {
  templates: OcrAnalyzerTemplate[]
}

/** Bulk response containing schemas for all registered node types. */
export interface AllNodeSchemasResponse {
  schema_version: number
  schemas: Record<string, NodeSchemaEntry>
}

export type WorkflowNodeConfigStatus = 'valid' | 'incomplete' | 'invalid' | 'unchecked'

export interface WorkflowNodeConfigIssue {
  code: string
  severity: 'error' | 'warning' | 'info'
  field?: string | null
  message: string
}

export interface WorkflowNodeConfigRecommendation {
  field?: string | null
  action: string
  rationale: string
  suggested_value?: unknown
  safe_to_apply?: boolean
}

export interface WorkflowNodeConfigValidateRequest {
  workflow_id?: string | null
  node_id: string
  node_type: string
  node_data: Record<string, unknown>
  graph_context?: Array<{
    id: string
    type?: string | null
    label?: string | null
    direction: 'in' | 'out'
    edge_label?: string | null
  }>
  skip_ai?: boolean
}

export interface WorkflowNodeConfigValidateResponse {
  status: WorkflowNodeConfigStatus
  schema_issues: WorkflowNodeConfigIssue[]
  ai_issues: WorkflowNodeConfigIssue[]
  recommendations: WorkflowNodeConfigRecommendation[]
  ai_skipped: boolean
  config_fingerprint: string
  node_id: string
  node_type: string
}

export interface WorkflowGraphHealthRequest {
  nodes: Array<{ id: string; type?: string | null; data?: Record<string, unknown> }>
  edges: Array<{ id?: string; source: string; target: string; sourceHandle?: string | null }>
}

/** Graph-level issues per node id (branches nothing selects, nodes that never run). */
export interface WorkflowGraphHealthResponse {
  issues_by_node: Record<string, WorkflowNodeConfigIssue[]>
  unreachable_node_count: number
}

/** Per-workflow execution / input_data reference (Run with Params + API docs). */
export interface WorkflowInputDataFieldSource {
  node_id: string
  node_type: string
  node_label?: string | null
  config_field?: string | null
  source_kind?: string
}

export interface WorkflowInputDataFieldRef {
  path: string
  type: string
  required: boolean
  default?: unknown
  description?: string | null
  sources: WorkflowInputDataFieldSource[]
}

export interface WorkflowInputDataReference {
  description?: string
  fields: WorkflowInputDataFieldRef[]
  example: Record<string, unknown>
  json_schema: Record<string, unknown>
  required_paths: string[]
  optional_paths: string[]
}

export interface WorkflowExecuteEndpointFieldDoc {
  name: string
  type: string
  required: boolean
  default?: unknown
  description: string
}

export interface WorkflowExecuteEndpointRef {
  method: string
  path: string
  versioned_path?: string | null
  request_body_fields: WorkflowExecuteEndpointFieldDoc[]
}

export interface WorkflowConsumerNodeRef {
  node_id: string
  node_type: string
  node_label?: string | null
}

export interface WorkflowExecutionReferenceResponse {
  schema_version: number
  workflow_id: string
  workflow_name: string
  version_id: string
  version_number: number
  version_source: 'published' | 'current'
  execute: WorkflowExecuteEndpointRef
  input_data: WorkflowInputDataReference
  consumer_nodes: WorkflowConsumerNodeRef[]
  trigger_notes: string[]
  openapi: Record<string, unknown>
}

/** Tenant AI Build (CWB) per-task LLM routing (admin). */
export interface CwbAiBuildLlmAssignment {
  model_selector: string
  provider: string
  model_id: string
  api_key_env?: string | null
  base_url?: string | null
  api_version?: string | null
}

export interface CwbAiBuildLlmSettingsRead {
  tenant_id: string
  assignments: Record<string, CwbAiBuildLlmAssignment | null>
  task_types: string[]
  task_labels: Record<string, string>
  /** null = not set (enabled unless platform denies). false = tenant opted out. */
  cwb_advanced_ai_enabled?: boolean | null
  /** TAGP platform flag (fail-open if unreachable). */
  cwb_advanced_ai_platform_allowed?: boolean
  /** Effective: env + platform + tenant. */
  cwb_advanced_ai_effective?: boolean
  /** PMA plan engine: tenant admin opt-in (null/false = standard CWB planning). */
  pma_engine_enabled?: boolean | null
  /** Env kill-switch (read-only for UI); true unless ops disabled it globally. */
  pma_engine_platform_allowed?: boolean
  /** Effective: env kill-switch + tenant column. */
  pma_engine_effective?: boolean
}

export interface CwbAiBuildLlmSettingsUpdate {
  assignments?: Record<string, CwbAiBuildLlmAssignment | null> | null
  cwb_advanced_ai_enabled?: boolean | null
  pma_engine_enabled?: boolean | null
}

/** Tenant floor for AI Workflows execution PII/PHI masking. */
export interface TenantPrivacySettingsRead {
  tenant_id: string
  enabled: boolean
  profile: 'minimal' | 'moderate' | 'strict' | 'hipaa'
  required_entities: string[]
  strategy: 'redact' | 'hash' | 'partial'
  compliance_mode: 'none' | 'soc2' | 'hipaa' | 'strict'
  created_at?: number | null
  updated_at?: number | null
}

export interface TenantPrivacySettingsUpdate {
  enabled?: boolean
  profile?: TenantPrivacySettingsRead['profile']
  required_entities?: string[]
  strategy?: TenantPrivacySettingsRead['strategy']
  compliance_mode?: TenantPrivacySettingsRead['compliance_mode']
}

/** POST /api/v1/blueprint-spec/generate — Blueprint library multi-stage AI pipeline. */
export interface BlueprintSpecGenerateResponse {
  spec: Record<string, unknown>
  raw_model_text?: string | null
  stages_completed?: string[]
  tokens_input?: number
  tokens_output?: number
  foundation_model_id?: string | null
  model_id?: string | null
  estimated_cost_usd?: number | null
}

/** SSE event from /blueprint-spec/generate-stream */
export interface BlueprintSpecStreamEvent {
  event: 'phase_a' | 'complete' | 'error' | 'done'
  spec?: Record<string, unknown>
  stages_completed?: string[]
  tokens_input?: number
  tokens_output?: number
  foundation_model_id?: string | null
  model_id?: string | null
  estimated_cost_usd?: number | null
  cached?: boolean
  message?: string
}

/** Shown when blueprint-spec SSE fails with a generic backend message (often missing tenant model). */
export const BLUEPRINT_SPEC_MODEL_CONFIG_HINT =
  'Select the Blueprint creation model in Admin → General settings → Workflow AI, then try again.'

const GENERIC_BLUEPRINT_SPEC_ERROR_MESSAGES = new Set([
  'Blueprint specification generation failed',
  'Blueprint generation failed',
])

function resolveBlueprintSpecStreamErrorMessage(raw: string | undefined): string {
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  if (!trimmed || GENERIC_BLUEPRINT_SPEC_ERROR_MESSAGES.has(trimmed)) {
    return BLUEPRINT_SPEC_MODEL_CONFIG_HINT
  }
  return trimmed
}

export interface WorkflowReadResponse {
  id: string
  name: string
  description: string | null
  project_id: string
  project_name?: string | null
  tenant_id: string
  status: WorkflowStatus
  current_version_id: string | null
  published_version_id?: string | null
  /** Current version number (from backend version_number) */
  version_number?: number
  execution_config: Record<string, unknown> | null
  created_by: string
  created_at: number
  updated_at: number
  graph_definition?: {
    nodes: WorkflowNode[]
    edges: WorkflowEdge[]
    node_groups?: WorkflowNodeGroup[]
    canvas_annotations?: WorkflowCanvasAnnotation[]
  }
  /** Schedule/trigger config for the current version (used when Start node trigger is schedule) */
  trigger_config?: WorkflowTriggerConfig | null
  /** Optimistic locking version — pass back on PUT to detect concurrent edits */
  lock_version?: number
  /** Backend may provide last execution summary */
  last_run_at?: number | null
  last_run_status?: string | null
  /** Id of that run, so a pending handoff can be cancelled from the canvas. */
  last_run_id?: string | null
  /** Total number of runs for this workflow (list endpoint) */
  run_count?: number
  /** Nodes on the current version (list endpoint; graph JSON is not included) */
  node_count?: number
  tags?: string[] | null
  /** Tenant workflow registry: unregistered | registered | archived */
  registry_status?: string | null
  /** Version pinned at registry registration time */
  registered_version_id?: string | null
  registered_version_number?: number | null
  /** Latest live schedule: true=active, false=paused, undefined/null=no Temporal job (list endpoint) */
  schedule_is_active?: boolean | null
  icon_file_id?: string | null
  /** Whether the current user has favourited this workflow (list endpoint) */
  is_fav?: boolean
  /** Whether the current version (current_version_id) is immutable — list/detail endpoints */
  current_version_is_immutable?: boolean | null
  /** Reference-resolution warnings when creating from a Solution Template */
  warnings?: string[]
  /** Registry catalog metadata (custom fields defined under Platform Admin → Configurations). */
  catalog_metadata?: Record<string, unknown> | null
}

export interface WorkflowListResponse {
  items: WorkflowReadResponse[]
  total: number
}

/** GET /workflows/registry — published native + external workflows for the tenant registry */
export type RegistryWorkflowType = 'native' | 'external'

export interface RegistryWorkflowListItem {
  id: string
  name: string
  description: string | null
  project_id: string
  project_name: string | null
  status: string
  created_by: string
  created_by_name: string | null
  created_at: number
  updated_at: number
  /** Optimistic lock from WorkflowListItem — registry edit still GETs detail before PUT */
  lock_version?: number
  version_number?: number | null
  tags?: string[] | null
  registry_status?: string | null
  is_fav?: boolean
  /** Discriminator: native AIWorkflows row vs TAGP external_workflows catalog row. */
  type: RegistryWorkflowType
  platform?: string | null
  architecture_diagram?: Record<string, unknown> | null
  catalog_metadata?: Record<string, unknown> | null
  version?: string | null
  tenant_id?: string | null
  /** Deployment / registry ENV badge when available. */
  environment?: string | null
}

export interface RegistryWorkflowListResponse {
  items: RegistryWorkflowListItem[]
  total: number
  page: number
  page_size: number
}

export interface RegistryWorkflowListParams {
  project_id?: string
  search?: string
  creator_id?: string
  workflow_type?: RegistryWorkflowType
  page?: number
  page_size?: number
  order_by?: string
  order_desc?: boolean
  is_favourite?: boolean
  scope?: 'all'
}

/** Response from GET .../workflows/export-import/{id}/dependencies */
export interface WorkflowDependencyItem {
  id: string
  display_name: string
}

export interface WorkflowDependenciesResponse {
  workflow: { id: string; name: string }
  agents: WorkflowDependencyItem[]
  models: WorkflowDependencyItem[]
  connectors: WorkflowDependencyItem[]
  iam: WorkflowDependencyItem[]
  tools: WorkflowDependencyItem[]
  prompts: WorkflowDependencyItem[]
  skills: WorkflowDependencyItem[]
  knowledge: WorkflowDependencyItem[]
  totals?: Record<string, number>
  prerequisites?: Array<{
    category: string
    portable_id: string
    bundled: boolean
    reason: string
  }>
}

export interface WorkflowPackageExportOptions {
  include_agents?: boolean
  include_agent_dependencies?: boolean
  include_workflow_deps?: boolean
  include_models?: boolean
  include_connectors?: boolean
  include_iam?: boolean
}

/** Single version from GET /workflows/{id}/versions */
export interface WorkflowVersionItem {
  id: string
  workflow_id: string
  version_number: number
  is_immutable: boolean
  is_archived?: boolean
  commit_message?: string | null
  created_by?: string | null
  created_by_name?: string | null
  created_at?: number
  updated_at?: number
  schedule_is_active?: boolean | null
}

/** Response from POST /workflows/{id}/new-version */
export interface WorkflowNewVersionResponse {
  id: string
  version_id: string
  version_number: number
  message: string
}

export interface ArchivedWorkflowVersionItem {
  id: string
  workflow_id: string
  workflow_name: string
  project_id: string
  project_name?: string | null
  version_number: number
  is_archived: boolean
  commit_message?: string | null
  created_by?: string | null
  created_by_name?: string | null
  created_at?: number
  updated_at?: number
  icon_file_id?: string | null
}

/** Single version with full snapshot from GET /workflows/{id}/versions/{version_id} */
export interface WorkflowVersionDetailResponse {
  id: string
  workflow_id: string
  version_number: number
  is_immutable: boolean
  is_archived?: boolean
  graph_definition?: { nodes: WorkflowNode[]; edges: WorkflowEdge[]; node_groups?: WorkflowNodeGroup[] }
  trigger_config?: WorkflowTriggerConfig | null
  execution_config?: Record<string, unknown> | null
  created_at?: number
  updated_at?: number
}

export interface ExecutionReadResponse {
  id: string
  workflow_id: string
  version_id: string | null
  version_number?: number | null
  orchestrator_job_id: string | null
  orchestrator_run_id: string | null
  /** Dynamically built link to the run in the orchestrator UI (Temporal, Databricks, etc.). Temporal: same origin as the API + `/temporal/...`, unless overridden. */
  orchestrator_run_url?: string | null
  status: 'pending' | 'running' | 'paused' | 'success' | 'failed' | 'cancelled' | 'timed_out'
  trigger_source: 'manual' | 'schedule' | 'webhook' | 'api' | 'storage' | 'database' | 'email' | 'workflow_store' | 'workflow_registry' | 'deployment'
  started_at: number | null
  completed_at: number | null
  started_by: string | null
  /** Resolved display name from AIWorkflows users lookup; falls back to started_by. */
  started_by_name?: string | null
  /** Last user who cancelled or resumed; defaults to started_by. */
  updated_by?: string | null
  updated_by_name?: string | null
  error_message: string | null
  execution_metadata?: ExecutionMetadata | null
  /** Frozen canvas graph compiled for this run. Historic Runs must use this. */
  graph_snapshot?: { nodes?: WorkflowNode[]; edges?: WorkflowEdge[] } | null
  created_at: number
  resumed_from_execution_id?: string | null
  resume_depth?: number
  resume_strategy?: string | null
  /** Latest workflow schedule cadence (list endpoints; schedule-triggered runs). */
  cron_expression?: string | null
  interval_seconds?: number | null
  schedule_type?: 'cron' | 'interval' | string | null
  schedule_is_active?: boolean | null
  schedule_timezone?: string | null
  /** Present when PII/PHI masking is active on the read path. */
  _privacy?: PrivacyMeta
}

export interface PrivacyMeta {
  enabled: boolean
  masked: boolean
  profile: string
  entities: string[]
  strategy: string
  compliance_mode: string
  can_unmask: boolean
}

export interface WorkflowPrivacyConfig {
  enabled?: boolean
  profile?: 'minimal' | 'moderate' | 'strict' | 'hipaa'
  entities?: string[]
  mask_paths?: string[]
  preserve_paths?: string[]
  field_annotations?: Record<string, string>
  strategy?: 'redact' | 'hash' | 'partial'
  compliance_mode?: 'none' | 'soc2' | 'hipaa' | 'strict'
}

export interface ExecutionMetadata {
  output?: Record<string, unknown>
  preview_mode?: boolean
  preview_limit?: number
  workflow_result?: {
    status?: string
    total_nodes?: number
    total_steps?: number
    completed_nodes?: string[]
    failed_nodes?: Array<{
      node_id: string
      node_name?: string | null
      error: string
      error_type?: string
      step_index?: number
    }>
    restored_nodes?: string[]
    rerun_nodes?: string[]
    resumed_from_execution_id?: string
    resume_strategy?: string
    resume_savings?: {
      nodes_restored?: number
      nodes_rerun?: number
      pct_nodes_saved?: number
    }
  }
  /** Epoch microseconds when delayed claim-check blobs will be deleted. */
  claim_check_available_until?: number
}

export type ResumeStrategy = 'FROM_FAILURE' | 'FROM_NODE' | 'RERUN_SELECTED'

export interface ResumePreviewResponse {
  resumable: boolean
  nodes_to_rerun: string[]
  nodes_to_restore: string[]
  estimated_cost_savings_pct: number
  version_match: boolean
  resume_depth: number
}

export interface ResumeExecutionRequest {
  strategy?: ResumeStrategy
  target_node_ids?: string[] | null
  rerun_dependents?: boolean
  trigger_source?: ExecutionReadResponse['trigger_source']
}

export interface ResumeExecutionResponse {
  execution_id: string
  status: string
  orchestrator_job_id?: string | null
  resumed_from_execution_id: string
  resume_strategy: string
  nodes_to_rerun: string[]
  nodes_restored: number
}

/**
 * Robustly extracts an error message from a workflow execution response.
 * Prioritizes the first failed node in execution_metadata, then falls back to
 * the top-level error_message.
 */
export function getExecutionError(execution: ExecutionReadResponse | null | undefined): string | null {
  if (!execution) return null

  const failedNodes = execution.execution_metadata?.workflow_result?.failed_nodes
  if (failedNodes && failedNodes.length > 0) {
    const errors = failedNodes.map((failedNode) => {
      const nodeDisplayName = failedNode.node_name || failedNode.node_id
      const nodeRef = nodeDisplayName ? `Node "${nodeDisplayName}"` : 'A node'
      return `${nodeRef} failed: ${failedNode.error}`
    })
    return errors.join('; ')
  }

  if (execution.error_message) return execution.error_message

  return null
}

/**
 * Extracts a list of all error messages from a workflow execution response.
 */
export function getExecutionErrorsList(execution: ExecutionReadResponse | null | undefined): string[] {
  if (!execution) return []

  const failedNodes = execution.execution_metadata?.workflow_result?.failed_nodes
  if (failedNodes && failedNodes.length > 0) {
    return failedNodes.map((failedNode) => {
      const nodeDisplayName = failedNode.node_name || failedNode.node_id
      const nodeRef = nodeDisplayName ? `Node "${nodeDisplayName}"` : 'A node'
      return `${nodeRef} failed: ${failedNode.error}`
    })
  }

  if (execution.error_message) return [execution.error_message]

  return []
}

/** Execution list item with workflow display info (global Runs view) */
export interface ExecutionListWithWorkflowResponse extends ExecutionReadResponse {
  workflow_name: string
  project_id: string
  project_name?: string | null
}

/** Response from POST /workflows/{id}/execute (backend returns execution_id, not id) */
export interface TriggerExecutionResponse {
  execution_id: string
  status: string
  orchestrator_job_id?: string | null
}

export interface NodeExecutionReadResponse {
  id: string
  execution_id: string
  node_id: string
  node_name?: string | null
  node_type: string
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped' | 'cancelled' | 'restored' | 'unknown'
  started_at: number | null
  completed_at: number | null
  output_ref?: string | null
  output_data?: Record<string, unknown> | null
  input_data?: Record<string, unknown> | null
  error_message: string | null
  retry_count: number
  has_pma_view?: boolean
}

export interface NodeDataSideResponse {
  raw: Record<string, unknown> | null
  preview: {
    columns: string[]
    rows: Record<string, unknown>[]
    total: number
    source_key?: string
  } | null
  tabular: boolean
  metrics: Record<string, unknown> | null
  expired?: boolean
  too_large?: boolean
  truncated?: boolean
  original_size_bytes?: number | null
}

export interface PmaViewProcessCard {
  index: number
  name?: string
  has_bpmn?: boolean
  has_future?: boolean
  risk_count?: number
  control_count?: number
}

export interface PmaViewCatalogResponse {
  processes: PmaViewProcessCard[]
  process_count: number
}

export interface PmaProcessAnalysisResponse {
  index?: number
  process_name?: string
  labels?: Record<string, string>
  risk_taxonomy?: unknown[]
  controls?: unknown[]
  process_improvements?: unknown[]
  dead_end_points?: unknown[]
  final_distinct_pools?: unknown[]
  agentification_insights?: unknown[]
  cross_document_conflicts?: unknown
  future_state?: Record<string, unknown>
}

export interface NodeExecutionDetailResponse {
  execution_id: string
  node_id: string
  node_name: string | null
  node_type: string
  status: string
  started_at: number | null
  duration_ms: number | null
  error: string | null
  input: NodeDataSideResponse
  output: NodeDataSideResponse
  /** Epoch microseconds when delayed claim-check blobs will be deleted. */
  claim_check_available_until?: number | null
  _privacy?: PrivacyMeta
}

export interface ScheduleReadResponse {
  id: string
  workflow_id: string
  version_id: string | null
  schedule_type: 'cron' | 'interval'
  cron_expression: string | null
  interval_seconds: number | null
  is_active: boolean
  last_run_at: number | null
  next_run_at: number | null
  timezone: string | null
  orchestrator_schedule_id: string | null
  created_at: number
}

export interface ScheduleCreatePayload {
  schedule_type: 'cron' | 'interval'
  cron_expression?: string | null
  interval_seconds?: number | null
  timezone?: string | null
}

/** Auto-trigger kind persisted on a workflow trigger row. */
export type WorkflowTriggerType = 'webhook' | 'storage' | 'schedule' | 'database' | 'email'

export interface TriggerRead {
  id: string
  workflow_id: string
  tenant_id: string
  version_id: string | null
  type: WorkflowTriggerType
  status: 'active' | 'paused' | 'disabled'
  config: Record<string, unknown>
  last_fired_at?: number | null
  last_error?: string | null
  last_poll_at?: number | null
  next_poll_at?: number | null
  created_by?: string | null
  run_as_user_id?: string | null
  run_as_user_email?: string | null
  created_at: number
  updated_at: number
}

export interface TriggerTokenRead {
  id: string
  trigger_id: string
  label?: string | null
  token_last4?: string | null
  created_by?: string | null
  run_as_user_id?: string | null
  run_as_user_email?: string | null
  expires_at?: number | null
  revoked_at?: number | null
  last_used_at?: number | null
  created_at: number
  updated_at: number
  has_run_as_credentials?: boolean
}

export interface TriggerTokenCreateResponse {
  token: string
  token_last4?: string | null
  token_id: string
}

export interface TriggerTokenCreatePayload {
  label?: string
  expires_at?: number | null
}

export interface TriggerTokenUpdatePayload {
  label?: string
  expires_at?: number | null
}

export interface TriggerTestPayload {
  payload?: Record<string, unknown>
}

export interface TriggerTestResponse {
  status: string
  event_id: string
}

export interface TriggerUpsertPayload {
  type: WorkflowTriggerType
  status?: 'active' | 'paused' | 'disabled'
  config: Record<string, unknown>
}

export interface TriggerStatusUpdatePayload {
  status: 'active' | 'paused' | 'disabled'
}

/** Unified runtime controls (triggers + Temporal schedule) for published workflows. */
export type WorkflowControlKind = 'trigger' | 'schedule'
export type WorkflowControlType = 'database' | 'email' | 'storage' | 'webhook' | 'schedule'
export type WorkflowControlStatus = 'active' | 'paused'
export type WorkflowControlsMasterStatus = 'active' | 'paused' | 'mixed' | 'none'

export interface WorkflowControlItem {
  id: string
  kind: WorkflowControlKind
  type: WorkflowControlType
  label: string
  status: WorkflowControlStatus
  last_fired_at?: number | null
  last_error?: string | null
  last_poll_at?: number | null
}

export interface WorkflowControlsRead {
  workflow_id: string
  master_status: WorkflowControlsMasterStatus
  items: WorkflowControlItem[]
}

export interface WorkflowControlStatusUpdatePayload {
  status: WorkflowControlStatus
}

export interface WorkflowControlsBulkResult {
  workflow_id: string
  master_status: WorkflowControlsMasterStatus
  updated: number
  items: WorkflowControlItem[]
}

export interface NodeRegistryItem {
  type: string
  category: string
  is_beta: boolean
  input_schema: Record<string, unknown>
  output_schema: Record<string, unknown>
}

// n8n Workflow Import types
export interface N8nImportWarning {
  node_id: string
  level: 'info' | 'low_confidence' | 'fallback'
  message: string
}

export interface N8nTranslatedNode {
  id: string
  type: string
  position: { x: number; y: number }
  data: Record<string, unknown>
}

export interface N8nTranslatedEdge {
  id: string
  source: string
  target: string
  sourceHandle?: string
  targetHandle?: string
}

export interface N8nImportRequest {
  n8n_json: Record<string, unknown>
  mode?: 'graph' | 'create'
  project_id?: string
  name?: string
}

export interface N8nImportResponse {
  nodes: N8nTranslatedNode[]
  edges: N8nTranslatedEdge[]
  warnings: N8nImportWarning[]
  workflow_id?: string | null
}

export interface UserToolCredential {
  id: string
  user_id?: string
  tenant_id?: string
  project_id?: string | null
  tool_type: string
  label: string
  auth_type: string
  base_url?: string | null
  extra_config?: Record<string, unknown> | null
  status: string
  expires_at?: number | null
  last_used_at?: number | null
  created_at: number
  updated_at: number
}

export interface UserToolCredentialList {
  items: UserToolCredential[]
  total: number
}

export interface UserToolCredentialCreatePayload {
  tool_type: string
  label?: string
  auth_type?: string
  credentials: Record<string, string>
  base_url?: string
  extra_config?: Record<string, unknown>
  /** Required: scopes the credential to the workflow's project for collaborative use. */
  project_id: string
}

export interface UserToolCredentialUpdatePayload {
  label?: string
  auth_type?: string
  credentials?: Record<string, string>
  base_url?: string
  extra_config?: Record<string, unknown>
  status?: string
  project_id?: string
}

export interface WorkflowImportEntityResult {
  action: 'created' | 'skipped' | 'failed'
  entity_type: string
  entity_name: string | null
  entity_id: string | null
  warnings: string[]
  errors: string[]
}

export interface WorkflowImportResult {
  success: boolean
  dry_run: boolean
  entities: WorkflowImportEntityResult[]
  summary: Record<string, Record<string, number>>
  errors: string[]
}

/** One dependency entity result from a package import (prompt/skill/agent/etc.). */
export interface PackageImportEntityResult {
  portable_id: string
  entity_type: string
  action: 'created' | 'matched' | 'placeholder' | 'skipped' | 'failed'
  entity_id?: string | null
  entity_name?: string | null
  path?: string
  warnings: string[]
  errors: string[]
}

/** Aggregate result of importing a workflow package ZIP. */
export interface WorkflowPackageImportResult {
  success: boolean
  dry_run: boolean
  dependencies: PackageImportEntityResult[]
  workflows: WorkflowImportResult[]
  prerequisites: Array<{
    category: string
    portable_id: string
    bundled: boolean
    reason: string
  }>
  configuration_warnings: Array<Record<string, unknown>>
  summary: Record<string, number>
  errors: string[]
  /** Resources skipped because the caller lacks export/import permission for that type. */
  warnings?: string[]
}

export interface WorkflowPackageImportOptions {
  dryRun?: boolean
  overwrite?: boolean
}

// =============================================================================
// METRICS TYPES
// =============================================================================

export interface NodeMetricsResponse {
  id: string
  execution_id: string
  node_execution_id: string | null
  node_id: string
  node_type: string
  duration_ms: number | null
  status: string | null
  retry_count: number
  error_type: string | null
  metrics: Record<string, unknown>
  created_at: number
}

export interface ExecutionMetricsResponse {
  id: string
  execution_id: string
  workflow_id: string
  version_id: string | null
  status: string | null
  trigger_source?: string | null
  total_duration_ms: number | null
  total_nodes: number | null
  completed_nodes: number | null
  failed_nodes: number | null
  total_retries: number | null
  total_tokens: number | null
  total_records: number | null
  /** Frozen USD estimate at run completion; absent on legacy fallback rows */
  estimated_cost_usd?: number | null
  llm_pricing_incomplete?: boolean
  /** Present on single-execution detail; omitted from aggregate list */
  llm_pricing_basis?: Record<string, unknown> | null
  metrics: Record<string, unknown>
  created_at: number
}

export interface MetricsSummary {
  total_runs: number
  success_count: number
  failed_count: number
  success_rate: number
  avg_duration_ms: number | null
  p90_duration_ms: number | null
  total_tokens: number
  total_records: number
  total_retries: number
  avg_retries: number
  /** Sum of per-run estimated_cost_usd when present */
  total_estimated_cost_usd?: number | null
}

export interface TriggerEventCounts {
  total: number
  pending: number
  processing: number
  dispatched: number
  failed: number
  dead_letter: number
}

export interface TriggerEventsSummary {
  total_events: number
  overall: TriggerEventCounts
  by_type: Record<string, TriggerEventCounts>
  avg_dispatch_latency_ms: number | null
  p90_dispatch_latency_ms: number | null
}

export interface TriggerDefinitionSummary {
  total: number
  active: number
  paused: number
  disabled: number
  by_type: Record<string, number>
}

export interface MetricsDailyBucket {
  date: string
  total_runs: number
  success_count: number
  failed_count: number
  retry_runs: number
  total_retries: number
  median_duration_ms?: number | null
  avg_duration_ms?: number | null
  total_tokens: number
  total_records: number
  estimated_cost_usd?: number | null
}

export interface MetricsWorkflowBucket {
  workflow_id: string
  total_runs: number
  failed_count: number
}

export interface MetricsAggregateResponse {
  workflow_id: string
  period: string
  total_runs: number
  summary: MetricsSummary
  executions: ExecutionMetricsResponse[]
  trigger_events?: TriggerEventsSummary
  trigger_definitions?: TriggerDefinitionSummary
  daily?: MetricsDailyBucket[]
  by_status?: Record<string, number>
  by_trigger?: Record<string, number>
  by_workflow?: MetricsWorkflowBucket[]
  executions_truncated?: boolean
}

export interface PreBuildCostSummary {
  total_events: number
  total_llm_calls: number
  total_input_tokens: number
  total_output_tokens: number
  total_tokens: number
  total_estimated_cost_usd?: number | null
}

export interface PreBuildCostEventRead {
  id: string
  occurred_at_us: number
  workflow_id?: string | null
  consumer_id?: string | null
  model_id: string
  input_tokens: number
  output_tokens: number
  llm_calls: number
  estimated_cost_usd?: number | null
  action: string
  extra: Record<string, unknown>
}

export interface PreBuildCostMetricsResponse {
  scope: string
  scope_id: string
  period: string
  consumer_type: string
  summary: PreBuildCostSummary
  events: PreBuildCostEventRead[]
  by_model: Array<{
    model_id: string
    calls: number
    input_tokens: number
    output_tokens: number
    estimated_cost_usd?: number
  }>
  by_action: Array<{ action: string; calls: number; tokens: number }>
  daily: Array<{ date: string; calls: number; tokens: number; estimated_cost_usd?: number }>
}

export interface ExecutionMetricsDetailResponse {
  execution: ExecutionMetricsResponse
  nodes: NodeMetricsResponse[]
}

// =============================================================================
// CWB (AI WORKFLOW BUILDER) METRICS TYPES
// =============================================================================

export interface CWBSessionMetricsRead {
  session_id: string
  project_id: string
  tenant_id: string
  user_id: string
  state: string
  mode: string
  message_count: number
  node_count: number
  llm_calls: number
  total_tokens: number
  workflow_id: string | null
  created_at: number
  updated_at: number
}

export interface CWBDailyMetrics {
  date: string
  sessions_created: number
  sessions_finalized: number
  total_messages: number
  total_llm_calls: number
  total_tokens: number
  unique_users: number
}

export interface CWBStateDistribution {
  state: string
  count: number
}

export interface CWBMetricsSummary {
  total_sessions: number
  active_sessions: number
  finalized_sessions: number
  finalization_rate: number
  total_messages: number
  total_llm_calls: number
  total_tokens: number
  avg_tokens_per_session: number
  avg_llm_calls_per_session: number
  avg_messages_per_session: number
  unique_users: number
}

export interface CWBMetricsAggregateResponse {
  scope: string
  scope_id: string
  period: string
  summary: CWBMetricsSummary
  daily: CWBDailyMetrics[]
  state_distribution: CWBStateDistribution[]
  sessions: CWBSessionMetricsRead[]
}

// =============================================================================
// WORKFLOW TEMPLATES (Solution Templates)
// =============================================================================

export interface WorkflowTemplatePreviewNode {
  node_type: string
  name: string
}

export interface WorkflowTemplateListItem {
  id: string
  name: string
  description: string | null
  icon: string | null
  is_archived: boolean
  created_by: string
  creator_name?: string
  preview_nodes?: WorkflowTemplatePreviewNode[]
  preview_node_total?: number
  created_at: number
  updated_at: number
}

export interface WorkflowTemplateRead {
  id: string
  name: string
  description: string | null
  icon: string | null
  tenant_id: string
  created_by: string
  creator_name?: string
  is_archived: boolean
  graph_definition: { nodes: WorkflowNode[]; edges: WorkflowEdge[] }
  trigger_config: WorkflowTriggerConfig | null
  execution_config: Record<string, unknown> | null
  created_at: number
  updated_at: number
}

export interface WorkflowTemplateListResponse {
  items: WorkflowTemplateListItem[]
  total: number
  offset: number
  limit: number
}

export interface WorkflowTemplateCreatePayload {
  name: string
  description?: string
  icon?: string
}

export interface WorkflowFromTemplateCreatePayload {
  template_id: string
  project_id: string
  name?: string
}

// =============================================================================
// QUICK-FIX TYPES
// =============================================================================

export interface QuickFixRequest {
  error_message: string
  error_type?: string | null
  node_type?: string | null
  node_id?: string | null
  node_config?: Record<string, unknown> | null
  model_id?: string | null
}

export interface QuickFixAction {
  label: string
  action_type:
    | 'open_vault'
    | 'open_connector'
    | 'open_iam'
    | 'open_model_settings'
    | 'open_node_config'
    | 'retry_execution'
    | 'open_docs'
    | 'none'
  target_id?: string | null
  url?: string | null
}

export interface QuickFixResponse {
  category: string
  friendly_title: string
  explanation: string
  severity: 'critical' | 'warning' | 'info'
  actions: QuickFixAction[]
  ai_suggestion: string | null
  raw_error: string
  source: 'registry' | 'ai'
}

// =============================================================================
// Node Suggestions
// =============================================================================

export interface GraphSkeletonNode {
  id: string
  type?: string
  data?: { label?: string }
}

export interface GraphSkeletonEdge {
  source: string
  target: string
}

export interface NextNodeSuggestionRequest {
  source_node_type: string | null
  has_start: boolean
  has_end: boolean
  node_count: number
  graph_skeleton?: {
    nodes: GraphSkeletonNode[]
    edges: GraphSkeletonEdge[]
  }
  workflow_id?: string | null
  selected_node_id?: string | null
}

export interface SuggestedNodeItem {
  node_type: string
  name: string
  category: string
  description: string
  is_beta: boolean
  score?: number | null
  reason_code?: string | null
}

export interface NextNodeSuggestionResponse {
  suggested: SuggestedNodeItem[]
  remaining: SuggestedNodeItem[]
  engine_version?: string | null
}

/** GET /api/v1/openapi/search — matched operations for Docs UI */
export interface OpenApiSearchOperationItem {
  operation_id: string
  method: string
  path: string
  summary?: string | null
  tags: string[]
}

export interface OpenApiSearchResponse {
  items: OpenApiSearchOperationItem[]
  total: number
}

// =============================================================================
// INTERNAL REQUEST HELPER
// =============================================================================

const { auth: authConfig } = config
const aiworkflowsFetch = createServiceFetch(config.api.aiworkflowsUrl)

function getAiworkflowsAuthHeaders(customToken?: string | null): Record<string, string> {
  const tokenProvided = arguments.length > 0
  let token: string | null = null
  if (tokenProvided) {
    token = customToken ?? null
  } else {
    try {
      token = localStorage.getItem(authConfig.tokenKey)
    } catch {
      // Continue without token
    }
  }

  let tenantId: string | null = null
  try {
    tenantId = localStorage.getItem('tagp-tenant-id')
  } catch {
    // Ignore
  }

  const assumedRoleId = getAssumedRoleIdForRequest()
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(tenantId && { 'X-Tenant-ID': tenantId }),
    ...(assumedRoleId && { 'X-Assume-Role-Id': assumedRoleId }),
  }
}

function tenantOverrideHeaders(tenantId?: string): Record<string, string> | undefined {
  if (!tenantId) return undefined
  return { 'X-Tenant-ID': tenantId }
}

function mergeAiworkflowsFetchHeaders(
  options: RequestInit,
  baseHeaders: Record<string, string> | Headers,
): Record<string, string> | Headers {
  if (options.headers instanceof Headers) {
    return new Headers({ ...baseHeaders, ...Object.fromEntries(options.headers.entries()) })
  }
  return { ...baseHeaders, ...(options.headers as Record<string, string> | undefined) }
}

function stripContentTypeForFormData(headers: Record<string, string> | Headers): void {
  if (headers instanceof Headers) {
    headers.delete('Content-Type')
    return
  }
  delete (headers as Record<string, string>)['Content-Type']
}

async function retryAiworkflowsFetchOn401(
  url: string,
  fetchConfig: RequestInit,
  response: Response,
): Promise<Response> {
  if (response.status !== 401) return response
  try {
    const newToken = await refreshAccessToken()
    const retryHeaders =
      fetchConfig.headers instanceof Headers
        ? new Headers(fetchConfig.headers)
        : { ...fetchConfig.headers }

    if (retryHeaders instanceof Headers) {
      retryHeaders.set('Authorization', `Bearer ${newToken}`)
    } else {
      (retryHeaders as Record<string, string>)['Authorization'] = `Bearer ${newToken}`
    }

    return aiworkflowsFetch(url, {
      ...fetchConfig,
      headers: retryHeaders,
    })
  } catch {
    return response
  }
}

async function executeAiworkflowsFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  let token: string | null = null
  try {
    token = localStorage.getItem(authConfig.tokenKey)
  } catch {
    // Continue without token
  }

  if (token && isTokenExpiringSoon(token)) {
    try {
      token = await refreshAccessToken()
    } catch {
      // Continue with current token
    }
  }

  const baseHeaders = getAiworkflowsAuthHeaders(token)
  const mergedHeaders = mergeAiworkflowsFetchHeaders(options, baseHeaders)

  if (options.body instanceof FormData) {
    stripContentTypeForFormData(mergedHeaders)
  }

  const fetchConfig: RequestInit = {
    ...options,
    headers: mergedHeaders,
  }

  const response = await aiworkflowsFetch(url, fetchConfig)
  return retryAiworkflowsFetchOn401(url, fetchConfig, response)
}

function parseAiworkflowsJsonBody<T>(text: string, status: number): T {
  try {
    return JSON.parse(text) as T
  } catch (parseError) {
    throw new ApiError('Invalid response format from server', status, {
      parseError,
      text: text.substring(0, 100),
    })
  }
}

/** App-root paths (e.g. /openapi.json), not under /api/v1. */
async function aiworkflowsRootRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const rootBase = config.api.aiworkflowsUrl.replace(/\/$/, '')
  const rel = path.startsWith('/') ? path : `/${path}`
  const url = `${rootBase}${rel}`

  const mergedHeaders =
    options.headers instanceof Headers
      ? new Headers({ Accept: 'application/json', ...Object.fromEntries(options.headers.entries()) })
      : { Accept: 'application/json', ...(options.headers as Record<string, string> | undefined) }

  const response = await executeAiworkflowsFetch(url, { ...options, headers: mergedHeaders })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const errorMessage = extractErrorMessage(errorData, `Request failed with status ${response.status}`)
    throw new ApiError(errorMessage, response.status, errorData)
  }

  const text = await response.text()
  if (!text || text.trim() === '') {
    return undefined as T
  }

  return parseAiworkflowsJsonBody<T>(text, response.status)
}

async function aiworkflowsRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const baseUrl = config.api.aiworkflowsUrl
  const url = `${baseUrl}/api/v1${endpoint}`

  const response = await executeAiworkflowsFetch(url, options)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const errorMessage = extractErrorMessage(errorData, `Request failed with status ${response.status}`)
    const apiErr = new ApiError(errorMessage, response.status, errorData)
    maybeCaptureApiError(url, apiErr)
    throw apiErr
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  if (!text || text.trim() === '') {
    return undefined as T
  }

  return parseAiworkflowsJsonBody<T>(text, response.status)
}

/** Metrics CSV export: format `created_at` from raw epoch microseconds in the downloaded file. */
async function fetchWorkflowMetricsExportCsvBlob(
  url: string,
  headers: Record<string, string>
): Promise<Blob> {
  const response = await aiworkflowsFetch(url, { headers })
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Export failed')
    throw new ApiError(errorText || `Export failed with status ${response.status}`, response.status)
  }
  const text = await response.text()
  const normalized = formatCreatedAtInMetricsExportCsv(text)
  return new Blob([normalized], { type: 'text/csv;charset=utf-8' })
}

// =============================================================================
// WORKFLOWS API
// =============================================================================

export interface SandboxCapabilitiesResponse {
  service: string
  sandbox: {
    code_sandbox: 'docker' | 'k8s' | 'disabled'
    isolation?: 'gvisor' | 'container' | 'disabled'
    reason?: string
    runtime_class?: string | null
    namespace?: string
    job_image?: string
    runtime?: string
  }
  /** OCR platform capabilities (GPU for EasyOCR/PaddleOCR; canvas schema discover). */
  ocr?: {
    gpu_available: boolean
    schema_auto_discover_enabled?: boolean
  }
}

/** Response from POST /workflows/analyze-dpi (OCR pdf_render_dpi autosuggest). */
export interface DpiAnalysisResponse {
  suggested_dpi: number
  detected_dpi: number | null
  source: string
  filename: string
  message: string
  page_width_pts?: number | null
  page_height_pts?: number | null
  image_width_px?: number | null
  image_height_px?: number | null
  file_kind?: string
}

// ── OCR node result (document viewer) ──────────────────────────────
export interface OcrRegionBboxPoint {
  x: number
  y: number
}

/** Typographic style tags on a region or word. */
export type OcrTextStyle = 'printed' | 'handwritten' | 'signature'

export interface OcrWord {
  text?: string
  bbox?: OcrRegionBboxPoint[]
  confidence?: number
  /** True when this word is handwritten / cursive / a signature stroke. */
  isHandwritten?: boolean
  /** Snake_case alias for `isHandwritten`. */
  is_handwritten?: boolean
  styles?: OcrTextStyle[]
}

export interface OcrRegion {
  text?: string
  type?: string
  confidence?: number
  bbox?: OcrRegionBboxPoint[]
  words?: OcrWord[]
  /** True when the block is handwritten rather than machine-printed. */
  isHandwritten?: boolean
  /** Snake_case alias for `isHandwritten`. */
  is_handwritten?: boolean
  styles?: OcrTextStyle[]
}

export type OcrSelectionMarkState = 'selected' | 'unselected'
export type OcrSelectionMarkKind = 'checkbox' | 'radio'

/** Checkbox or radio button classified separately from character OCR. */
export interface OcrSelectionMark {
  /** Whether the mark is ticked/filled (`selected`) or empty (`unselected`). */
  state: OcrSelectionMarkState
  /** Classifier confidence on a 0–1 scale. */
  confidence: number
  /** 4-corner polygon in the same coordinate space as region `bbox`. */
  polygon: OcrRegionBboxPoint[]
  /** Structural type: square checkbox vs circular radio button. */
  kind?: OcrSelectionMarkKind
}

export interface OcrPageRouting {
  complexity?: string
  contentType?: string
  tier?: string
  engine?: string
  modelId?: string | null
  promptProfile?: string
  strategy?: string
  score?: number
  confidence?: number
  reason?: string
  signals?: Record<string, unknown>
  escalated?: boolean
  skippedVlm?: boolean
}

export interface OcrPageResult {
  page: number
  text: string
  /** Engine confidence on 0–1 scale (legacy). */
  confidence?: number
  /** Per-page confidence on 0–100 scale for UI filtering. */
  confidence_score?: number
  language?: string
  regions?: OcrRegion[]
  /** Pixel dimensions of the image space the region bbox coords live in (0 = unknown). */
  width?: number
  height?: number
  /** Checkboxes / radio buttons (Azure DI-compatible camelCase key). */
  selectionMarks?: OcrSelectionMark[]
  /** Snake_case alias for `selectionMarks` (same payload). */
  selection_marks?: OcrSelectionMark[]
  /** Present when in-node dynamic routing ran for this page. */
  routing?: OcrPageRouting
}

/**
 * A word filled in by Contextual Word Prediction: the degraded/illegible visual
 * read (`original`) was masked and replaced with an LLM-predicted word (`predicted`)
 * inferred from surrounding semantics. Surfaced so the viewer can flag AI-predicted words.
 */
export interface OcrPredictedWord {
  /** The garbled/illegible token the OCR engine produced. */
  original: string
  /** The word the LLM predicted from surrounding context. */
  predicted: string
  /** Per-word OCR confidence (0–1) when the engine exposed it, else null. */
  confidence?: number | null
  /** Why the word was flagged: 'low_confidence' or 'illegible'. */
  reason?: string
  /** 1-based page number the word appears on. */
  page?: number
}

export interface OcrStructuredFieldSource {
  page?: number
  bbox?: [number, number, number, number]
  spans?: { offset: number; length: number }
}

export interface OcrResult {
  text: string
  engine: string
  ocr_applied: boolean
  confidence?: number
  /** Document-level confidence on 0–100 scale. */
  confidence_score?: number
  character_count?: number
  word_count?: number
  page_count?: number
  pages?: OcrPageResult[]
  metadata?: Record<string, unknown>
  extracted_queries?: Record<string, OcrExtractedQueryResult>
  structured_extraction?: {
    mode?: string
    data?: Record<string, unknown>
    confidence?: Record<string, number>
    source?: Record<string, OcrStructuredFieldSource>
    pages?: Array<{
      page?: number
      data?: Record<string, unknown>
      confidence?: Record<string, number>
      source?: Record<string, OcrStructuredFieldSource>
      hitl_review_fields?: Array<
        | string
        | {
            field: string
            primary_value?: string
            secondary_value?: string
            similarity_score?: number
            reason?: string
          }
      >
      attempts?: number
      error?: string
    }>
    hitl_review_fields?: Array<
      | string
      | {
          field: string
          primary_value?: string
          secondary_value?: string
          similarity_score?: number
          reason?: string
        }
    >
    extraction_only?: boolean
  }
  analyzer?: {
    template_id?: string
    family?: string
    document_type?: string
    document_type_confidence?: number
    schema_id?: string
    schema_overridden?: boolean
    capabilities_run?: string[]
  }
  hitl_review_fields?: string[]
  consensus_verification?: {
    total_fields_checked?: number
    agreed_count?: number
    discrepancy_count?: number
    skipped_count?: number
    verifications?: Array<{
      field?: string
      primary_read?: string
      secondary_read?: string
      similarity?: number
      status?: string
      reason?: string
    }>
  }
  /** Words filled in by Contextual Word Prediction (present only when enabled and words were predicted). */
  predicted_words?: OcrPredictedWord[]
  error?: string
  error_code?: string
}

export interface OcrExtractedQueryResult {
  query: string
  extracted_value: string | null
  /** False when the answer was not found in the document. */
  found?: boolean
  /** Omitted or null when the query answer was not found. */
  confidence_score?: number | null
  source_bounding_box: [number, number, number, number]
  page_number: number
}

export interface OcrResultFile {
  name?: string
  file_name?: string
  path?: string
  mime_type?: string
  content_type?: string
  /** Platform file-storage id (uploaded files); fetched via /api/files/{id}. */
  file_id?: string
  /** Registered storage-folder id (cloud-storage sources); fetched via the connector download endpoint. */
  registered_folder_id?: string
  /** Presigned download URL (cloud-storage sources). */
  presigned_url?: string
  /** Inline bytes — only present as a fallback when no reference is available. */
  content_base64?: string
  /** Rasterized PDF in platform storage for inline viewer preview (PowerPoint). */
  preview_file_id?: string
  /** MIME type of the preview document; PowerPoint previews are application/pdf. */
  preview_mime_type?: string
  ocr_result?: OcrResult
  [key: string]: unknown
}

export interface OcrResultResponse {
  files: OcrResultFile[]
  count: number
  ocr_applied_count: number
  _metrics: Record<string, unknown> | null
  truncated?: boolean
  original_size_bytes?: number | null
  error?: string
  detail?: string
}

interface ListWorkflowsQueryParams {
  project_id?: string | string[]
  status?: WorkflowStatus | WorkflowStatus[]
  search?: string
  skip?: number
  limit?: number
  created_by?: string | string[]
  scheduled?: boolean
  schedule_active?: boolean
  schedule_states?: string[]
  created_at_from?: number
  created_at_to?: number
  last_run_at_from?: number
  last_run_at_to?: number
  last_run_status?:
    | 'success' | 'failed' | 'running' | 'never_run'
    | ('success' | 'failed' | 'running' | 'never_run')[]
  has_published_version?: boolean
  tags?: string[]
  order_by?: 'name' | 'status' | 'created_at' | 'updated_at' | 'created_by' | 'last_run_at'
  order_desc?: boolean
  is_favourite?: boolean
  fields?: string
  include_execution?: boolean
}

function appendRepeatedQueryParam(
  searchParams: URLSearchParams,
  key: string,
  value?: string | string[],
): void {
  if (value === undefined) return
  for (const entry of Array.isArray(value) ? value : [value]) {
    if (entry) searchParams.append(key, entry)
  }
}

function appendRegistryListQueryParams(
  searchParams: URLSearchParams,
  params?: RegistryWorkflowListParams,
): void {
  if (!params) return
  if (params.project_id) searchParams.set('project_id', params.project_id)
  if (params.search) searchParams.set('search', params.search)
  if (params.creator_id) searchParams.set('creator_id', params.creator_id)
  if (params.workflow_type) searchParams.set('workflow_type', params.workflow_type)
  if (params.page !== undefined) searchParams.set('page', String(params.page))
  if (params.page_size !== undefined) searchParams.set('page_size', String(params.page_size))
  if (params.order_by) searchParams.set('order_by', params.order_by)
  if (params.order_desc !== undefined) searchParams.set('order_desc', String(params.order_desc))
  if (params.is_favourite !== undefined) searchParams.set('is_favourite', String(params.is_favourite))
  if (params.scope) searchParams.set('scope', params.scope)
}

function appendListWorkflowsQueryParams(
  searchParams: URLSearchParams,
  params?: ListWorkflowsQueryParams,
): void {
  appendRepeatedQueryParam(searchParams, 'project_id', params?.project_id)
  appendRepeatedQueryParam(searchParams, 'status', params?.status)
  if (params?.search) searchParams.set('search', params.search)
  if (params?.skip !== undefined) searchParams.set('offset', String(params.skip))
  if (params?.limit !== undefined) searchParams.set('limit', String(params.limit))
  appendRepeatedQueryParam(searchParams, 'created_by', params?.created_by)
  if (params?.scheduled !== undefined) searchParams.set('scheduled', String(params.scheduled))
  if (params?.schedule_active !== undefined)
    searchParams.set('schedule_active', String(params.schedule_active))
  appendRepeatedQueryParam(searchParams, 'schedule_states', params?.schedule_states)
  if (params?.created_at_from !== undefined) searchParams.set('created_at_from', String(params.created_at_from))
  if (params?.created_at_to !== undefined) searchParams.set('created_at_to', String(params.created_at_to))
  if (params?.last_run_at_from !== undefined) searchParams.set('last_run_at_from', String(params.last_run_at_from))
  if (params?.last_run_at_to !== undefined) searchParams.set('last_run_at_to', String(params.last_run_at_to))
  appendRepeatedQueryParam(searchParams, 'last_run_status', params?.last_run_status)
  if (params?.has_published_version !== undefined)
    searchParams.set('has_published_version', String(params.has_published_version))
  if (params?.tags?.length) params.tags.forEach((t) => searchParams.append('tags', t))
  if (params?.order_by) searchParams.set('order_by', params.order_by)
  if (params?.order_desc !== undefined) searchParams.set('order_desc', String(params.order_desc))
  if (params?.is_favourite !== undefined) searchParams.set('is_favourite', String(params.is_favourite))
  if (params?.fields) searchParams.set('fields', params.fields)
  if (params?.include_execution !== undefined)
    searchParams.set('include_execution', String(params.include_execution))
}

/** Decode `X-Package-Warnings` (URL-encoded JSON string list) from a package export. */
export function readPackageExportWarnings(header: string | null): string[] {
  if (!header) return []
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(header))
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (item): item is string => typeof item === 'string' && item.trim().length > 0,
    )
  } catch {
    return []
  }
}

function withDeploymentEnvironment(path: string, environment?: string): string {
  const env = (environment || '').trim()
  if (!env) return path
  const join = path.includes('?') ? '&' : '?'
  return `${path}${join}deployment_environment=${encodeURIComponent(env)}`
}

export const aiworkflowsApi = {
  getSandboxCapabilities: (): Promise<SandboxCapabilitiesResponse> =>
    aiworkflowsRequest<SandboxCapabilitiesResponse>('/capabilities'),

  // -------------------------------------------------------------------------
  // Workflow CRUD
  // -------------------------------------------------------------------------

  listWorkflows: (params?: ListWorkflowsQueryParams) => {
    const searchParams = new URLSearchParams()
    appendListWorkflowsQueryParams(searchParams, params)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<WorkflowListResponse>(`/workflows${query}`, { cache: 'no-store' })
  },

  listRegistryWorkflows: (params?: RegistryWorkflowListParams) => {
    const searchParams = new URLSearchParams()
    appendRegistryListQueryParams(searchParams, params)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<RegistryWorkflowListResponse>(`/workflows/registry${query}`)
  },

  registerWorkflowToRegistry: (workflowId: string) =>
    aiworkflowsRequest<WorkflowReadResponse>(`/workflows/${workflowId}/register`, {
      method: 'POST',
      body: '{}',
    }),

  listRegistryHistory: (workflowId: string) =>
    aiworkflowsRequest<{
      items: Array<{
        id: string
        workflow_id: string
        workflow_name: string
        status: string
        requested_by: string
        requested_by_name?: string | null
        reviewed_by?: string | null
        reviewed_by_name?: string | null
        reviewed_at?: number | null
        rejection_reason?: string | null
        created_at?: number | null
      }>
      total: number
    }>(`/workflows/${workflowId}/registry-requests`),

  listWorkflowRegistryRequests: (params?: {
    status?: string
    page?: number
    page_size?: number
    search?: string
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.page !== undefined) searchParams.set('page', String(params.page))
    if (params?.page_size !== undefined) searchParams.set('page_size', String(params.page_size))
    if (params?.search) searchParams.set('search', params.search)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<{
      items: Array<{
        id: string
        workflow_id: string
        workflow_name: string
        project_id?: string | null
        project_name?: string | null
        status: string
        requested_by: string
        requested_by_name?: string | null
        reviewed_by?: string | null
        reviewed_by_name?: string | null
        reviewed_at?: number | null
        rejection_reason?: string | null
        previous_registry_status?: string
        created_at?: number | null
        updated_at?: number | null
      }>
      total: number
    }>(`/workflows/registry-requests${query}`)
  },

  approveWorkflowRegistryRequest: (requestId: string) =>
    aiworkflowsRequest(`/workflows/registry-requests/${requestId}/approve`, {
      method: 'POST',
      body: '{}',
    }),

  rejectWorkflowRegistryRequest: (requestId: string, reason: string) =>
    aiworkflowsRequest(`/workflows/registry-requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  bulkDecideWorkflowRegistryRequests: (params: {
    items: Array<{ request_id: string }>
    action: 'approve' | 'reject'
    reason?: string
  }) =>
    aiworkflowsRequest<{ processed: number; skipped: number; errors: string[] }>(
      '/workflows/registry-requests/bulk-decide',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    ),

  unregisterWorkflowFromRegistry: (workflowId: string, options?: { tenantId?: string }) =>
    aiworkflowsRequest<WorkflowReadResponse>(`/workflows/${workflowId}/unregister`, {
      method: 'POST',
      body: '{}',
      headers: tenantOverrideHeaders(options?.tenantId),
    }),

  listArchivedRegistryWorkflows: (params?: RegistryWorkflowListParams) => {
    const searchParams = new URLSearchParams()
    appendRegistryListQueryParams(searchParams, params)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<RegistryWorkflowListResponse>(`/workflows/registry/archived${query}`)
  },

  reregisterWorkflow: (workflowId: string, options?: { tenantId?: string }) =>
    aiworkflowsRequest<WorkflowReadResponse>(`/workflows/${workflowId}/reregister`, {
      method: 'POST',
      body: '{}',
      headers: tenantOverrideHeaders(options?.tenantId),
    }),

  exportRegistryCsv: async (params?: {
    project_id?: string
    search?: string
    registry_status?: 'registered' | 'archived'
  }): Promise<{ blob: Blob; filename: string }> => {
    const searchParams = new URLSearchParams()
    if (params?.project_id) searchParams.set('project_id', params.project_id)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.registry_status) searchParams.set('registry_status', params.registry_status)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/workflows/registry/export.csv${query}`
    const response = await executeAiworkflowsFetch(url, {
      headers: { Accept: 'text/csv' },
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new ApiError(
        extractErrorMessage(errorData, `Export failed (${response.status})`),
        response.status,
        errorData,
      )
    }
    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition') || ''
    const match = disposition.match(/filename="?([^";]+)"?/)
    return {
      blob,
      filename: match?.[1] || 'Workflow_Registry.csv',
    }
  },

  downloadRegistryCsvTemplate: async (): Promise<{ blob: Blob; filename: string }> => {
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/workflows/registry/csv-template`
    const response = await executeAiworkflowsFetch(url, {
      headers: { Accept: 'text/csv' },
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new ApiError(
        extractErrorMessage(errorData, `Template download failed (${response.status})`),
        response.status,
        errorData,
      )
    }
    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition') || ''
    const match = disposition.match(/filename="?([^";]+)"?/)
    return {
      blob,
      filename: match?.[1] || 'Workflow_Registry_Template.csv',
    }
  },

  importRegistryCsv: (file: File, params?: { project_id?: string }) => {
    const form = new FormData()
    form.append('file', file)
    const searchParams = new URLSearchParams()
    if (params?.project_id) searchParams.set('project_id', params.project_id)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<{
      total_rows: number
      created: number
      updated?: number
      skipped: number
      duplicate_count?: number
      duplicates?: Array<{
        row_number: number
        name: string
        version: string
        project_id?: string | null
        existing_id?: string | null
        existing_type?: string | null
        existing_version?: string | null
        reason?: string
      }>
      errors: string[]
    }>(`/workflows/registry/import-csv${query}`, {
      method: 'POST',
      body: form,
    })
  },

  getWorkflow: (id: string, options?: { triggerSource?: string; deploymentEnvironment?: string }) => {
    const params = new URLSearchParams()
    if (options?.triggerSource) {
      params.set('triggerSource', options.triggerSource)
    }
    if (options?.deploymentEnvironment) {
      params.set('deploymentEnvironment', options.deploymentEnvironment)
    }
    const qs = params.toString()
    const querySuffix = qs ? `?${qs}` : ''
    return aiworkflowsRequest<WorkflowReadResponse>(
      `/workflows/${id}${querySuffix}`
    )
  },

  getWorkflowExecutionReference: (
    workflowId: string,
    options?: { version?: 'published' | 'current' }
  ) => {
    const version = options?.version ?? 'published'
    return aiworkflowsRequest<WorkflowExecutionReferenceResponse>(
      `/workflows/${workflowId}/execution-reference?version=${encodeURIComponent(version)}`
    )
  },

  /**
   * Lightweight status poll: returns run-status fields only (no graph_definition,
   * no trigger_config, no creator enrichment). Used by the open-canvas poll so it
   * cannot perturb in-flight canvas edits or carry heavy graph JSON every few seconds.
   */
  getWorkflowStatus: (id: string) =>
    aiworkflowsRequest<WorkflowReadResponse>(`/workflows/${id}?fields=status`),

  createWorkflow: (data: WorkflowCreatePayload) =>
    aiworkflowsRequest<WorkflowReadResponse>('/workflows', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createWorkflowFromTemplate: (data: WorkflowFromTemplateCreatePayload) =>
    aiworkflowsRequest<WorkflowReadResponse>('/workflows/from-template', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  checkWorkflowNameUniqueness: (name: string, excludeId?: string) => {
    const params = new URLSearchParams()
    params.append('name', name)
    if (excludeId) params.append('exclude_id', excludeId)
    return aiworkflowsRequest<{ is_unique: boolean; message: string | null }>(
      `/workflows/check-name?${params.toString()}`
    )
  },

  updateWorkflow: (id: string, data: WorkflowUpdatePayload, silent?: boolean) =>
    aiworkflowsRequest<WorkflowReadResponse>(
      `/workflows/${id}${silent ? '?silent=1' : ''}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    ),

  generateWorkflowDescription: (id: string, data?: WorkflowGenerateDescriptionPayload) =>
    aiworkflowsRequest<WorkflowGenerateDescriptionResponse>(
      `/workflows/${id}/generate-description`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      }
    ),

  generateWorkflowReversePrompt: (id: string, data?: WorkflowReversePromptPayload) =>
    aiworkflowsRequest<WorkflowReversePromptResponse>(`/workflows/${id}/reverse-prompt`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),

  deleteWorkflow: (id: string) =>
    aiworkflowsRequest<{ message: string; id: string }>(`/workflows/${id}`, {
      method: 'DELETE',
    }),

  unarchiveWorkflow: (id: string, data?: { name?: string }) =>
    aiworkflowsRequest<WorkflowReadResponse>(`/workflows/${id}/unarchive`, {
      method: 'POST',
      body: JSON.stringify(data?.name ? { name: data.name } : {}),
    }),

  /** Remove all persisted incremental markers (all nodes) for this workflow.
   *  Bounded by a client-side timeout so the confirm dialog can never hang on
   *  "Processing…" if the backend is slow/unreachable (it fails and closes instead). */
  clearAllWorkflowMarkers: (workflowId: string) =>
    aiworkflowsRequest<{ ok: boolean; deleted_count: number }>(`/workflows/${workflowId}/markers`, {
      method: 'DELETE',
      signal: AbortSignal.timeout(20000),
    }),

  /** Remove the persisted incremental marker for a single node (File Reader, SQL Reader, …). */
  clearNodeMarker: (workflowId: string, nodeId: string) =>
    aiworkflowsRequest<{ ok: boolean; deleted_count: number }>(
      `/workflows/${workflowId}/nodes/${encodeURIComponent(nodeId)}/marker`,
      {
        method: 'DELETE',
        signal: AbortSignal.timeout(20000),
      },
    ),

  publishWorkflow: (id: string, body?: { commit_message?: string }) => {
    let refreshToken: string | null = null
    try {
      refreshToken = localStorage.getItem('tagp-refresh-token')
    } catch {
      /* continue without */
    }
    return aiworkflowsRequest<{ version_id: string; version_number: number }>(`/workflows/${id}/publish`, {
      method: 'POST',
      headers: { ...(refreshToken && { 'X-Refresh-Token': refreshToken }) },
      body: JSON.stringify(body ?? {}),
    })
  },

  listWorkflowVersions: (workflowId: string) =>
    aiworkflowsRequest<WorkflowVersionItem[]>(`/workflows/${workflowId}/versions`),

  createNewVersion: (workflowId: string) =>
    aiworkflowsRequest<WorkflowNewVersionResponse>(`/workflows/${workflowId}/new-version`, {
      method: 'POST',
      body: '{}',
    }),

  forkWorkflowVersion: (workflowId: string, versionId: string) =>
    aiworkflowsRequest<WorkflowNewVersionResponse>(
      `/workflows/${workflowId}/versions/${versionId}/fork`,
      { method: 'POST', body: '{}' }
    ),

  archiveWorkflowVersion: (workflowId: string, versionId: string) =>
    aiworkflowsRequest<WorkflowVersionItem>(
      `/workflows/${workflowId}/versions/${versionId}/archive`,
      { method: 'POST', body: '{}' },
    ),

  restoreWorkflowVersion: (workflowId: string, versionId: string) =>
    aiworkflowsRequest<WorkflowVersionItem>(
      `/workflows/${workflowId}/versions/${versionId}/restore`,
      { method: 'POST', body: '{}' },
    ),

  listArchivedWorkflowVersions: (params?: {
    search?: string
    page?: number
    page_size?: number
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.page !== undefined) searchParams.set('page', String(params.page))
    if (params?.page_size !== undefined) searchParams.set('page_size', String(params.page_size))
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<{
      items: ArchivedWorkflowVersionItem[]
      total: number
      page: number
      page_size: number
    }>(`/workflows/archived-versions${query}`)
  },

  /** Point the editor at an existing draft. Does not create a version. */
  switchToDraftVersion: (workflowId: string, versionId: string) =>
    aiworkflowsRequest<WorkflowNewVersionResponse>(
      `/workflows/${workflowId}/versions/${versionId}/switch`,
      { method: 'POST', body: '{}' }
    ),

  /** Fork workflow: new workflow (same name, new version). Returns the created workflow. */
  cloneWorkflow: (workflowId: string, body?: { project_id?: string }) =>
    aiworkflowsRequest<WorkflowReadResponse>(`/workflows/${workflowId}/clone`, {
      method: 'POST',
      body: JSON.stringify(
        body?.project_id !== undefined && String(body.project_id).trim() !== ''
          ? { project_id: String(body.project_id).trim() }
          : {}
      ),
    }),

  getWorkflowVersion: (workflowId: string, versionId: string) =>
    aiworkflowsRequest<WorkflowVersionDetailResponse>(
      `/workflows/${workflowId}/versions/${versionId}`
    ),

  // -------------------------------------------------------------------------
  // Executions
  // -------------------------------------------------------------------------

  triggerExecution: (
    workflowId: string,
    params?: Record<string, unknown>,
    options?: {
      deployment_id?: string
      deployment_environment?: string
      trigger_source?: string
      preview_mode?: boolean
      preview_limit?: number
      run_draft?: boolean
      // Partial / selective node runs
      target_node_ids?: string[]
      include_upstream?: boolean
      node_test_inputs?: Record<string, Record<string, unknown>>
      force_test_input_node_ids?: string[]
      versionNumber?: number
    }
  ) => {
    let refreshToken: string | null = null
    try {
      refreshToken = localStorage.getItem('tagp-refresh-token')
    } catch {
      // Continue without refresh token
    }
    const versionNumber = options?.versionNumber
    const executePath =
      versionNumber != null
        ? `/workflows/${workflowId}/versions/${versionNumber}/execute`
        : `/workflows/${workflowId}/execute`
    return aiworkflowsRequest<TriggerExecutionResponse>(executePath, {
      method: 'POST',
      headers: {
        ...(refreshToken && { 'X-Azure-Refresh-Token': refreshToken }),
      },
      body: JSON.stringify({
        trigger_source: options?.trigger_source ?? 'manual',
        input_data: params || {},
        ...(options?.deployment_id ? { deployment_id: options.deployment_id } : {}),
        ...(options?.deployment_environment ? { deployment_environment: options.deployment_environment } : {}),
        ...(options?.preview_mode ? { preview_mode: true, preview_limit: options.preview_limit ?? 3 } : {}),
        ...(options?.run_draft ? { run_draft: true } : {}),
        ...(options?.target_node_ids && options.target_node_ids.length > 0
          ? {
              target_node_ids: options.target_node_ids,
              include_upstream: options.include_upstream ?? false,
            }
          : {}),
        ...(options?.node_test_inputs && Object.keys(options.node_test_inputs).length > 0
          ? { node_test_inputs: options.node_test_inputs }
          : {}),
        ...(options?.force_test_input_node_ids && options.force_test_input_node_ids.length > 0
          ? { force_test_input_node_ids: options.force_test_input_node_ids }
          : {}),
      }),
    })
  },

  listExecutions: (workflowId: string, params?: {
    offset?: number
    limit?: number
    search?: string
    deployment_environment?: string
    deployment_id?: string
    trigger_source?: string
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.offset !== undefined) searchParams.set('offset', String(params.offset))
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit))
    if (params?.search) searchParams.set('search', params.search)
    if (params?.deployment_environment) searchParams.set('deployment_environment', params.deployment_environment)
    if (params?.deployment_id) searchParams.set('deployment_id', params.deployment_id)
    if (params?.trigger_source) searchParams.set('trigger_source', params.trigger_source)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<{
      items: ExecutionReadResponse[]
      total: number
      offset?: number
      limit?: number
    }>(`/workflows/${workflowId}/executions${query}`)
  },

  /** List executions across all workflows for the tenant (Runs view), latest first */
  listAllExecutions: (params?: {
    project_id?: string
    offset?: number
    limit?: number
    search?: string
    status?: string
    trigger_source?: string
    started_by?: string
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.project_id) searchParams.set('project_id', params.project_id)
    if (params?.offset !== undefined) searchParams.set('offset', String(params.offset))
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit))
    if (params?.search) searchParams.set('search', params.search)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.trigger_source) searchParams.set('trigger_source', params.trigger_source)
    if (params?.started_by) searchParams.set('started_by', params.started_by)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<{
      items: ExecutionListWithWorkflowResponse[]
      total: number
      offset: number
      limit: number
    }>(`/workflows/executions${query}`)
  },

  /** Platform Admin only: pending/running executions across the tenant (Active Runs tab). */
  listActiveExecutions: (params?: {
    offset?: number
    limit?: number
    search?: string
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.offset !== undefined) searchParams.set('offset', String(params.offset))
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit))
    if (params?.search) searchParams.set('search', params.search)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<{
      items: ExecutionListWithWorkflowResponse[]
      total: number
      offset: number
      limit: number
    }>(`/workflows/executions/active${query}`)
  },

  /** Platform Admin only: terminate one or more in-progress executions. */
  terminateExecutionsBulk: (executionIds: string[]) =>
    aiworkflowsRequest<{
      results: Array<{ execution_id: string; ok: boolean; error?: string | null }>
      terminated: number
      failed: number
    }>(`/workflows/executions/terminate-bulk`, {
      method: 'POST',
      body: JSON.stringify({ execution_ids: executionIds }),
    }),

  getExecution: (executionId: string, options?: { deploymentEnvironment?: string }) =>
    aiworkflowsRequest<ExecutionReadResponse>(
      withDeploymentEnvironment(`/workflows/executions/${executionId}`, options?.deploymentEnvironment)
    ),

  getExecutionNodes: async (executionId: string, opts?: { unmask?: boolean; deploymentEnvironment?: string }) => {
    const params = new URLSearchParams()
    if (opts?.unmask) params.set('unmask', 'true')
    if (opts?.deploymentEnvironment) params.set('deployment_environment', opts.deploymentEnvironment)
    const q = params.toString() ? `?${params.toString()}` : ''
    const res = await aiworkflowsRequest<{ items: NodeExecutionReadResponse[]; _privacy?: PrivacyMeta }>(
      `/workflows/executions/${executionId}/nodes${q}`
    )
    return { items: res.items ?? [], _privacy: res._privacy }
  },

  getNodeOutputPreview: (executionId: string, nodeId: string, maxRows = 20, opts?: { unmask?: boolean; deploymentEnvironment?: string }) => {
    const unmask = opts?.unmask ? '&unmask=true' : ''
    const env = opts?.deploymentEnvironment
      ? `&deployment_environment=${encodeURIComponent(opts.deploymentEnvironment)}`
      : ''
    return aiworkflowsRequest<{
      preview: {
        source_key: string
        columns: string[]
        rows: Record<string, unknown>[]
        total: number
      } | null
      tabular: boolean
      _metrics: Record<string, unknown> | null
      expired?: boolean
      too_large?: boolean
      truncated?: boolean
      original_size_bytes?: number | null
      error?: string
      detail?: string
      _privacy?: PrivacyMeta
    }>(
      `/workflows/executions/${executionId}/nodes/${encodeURIComponent(nodeId)}/preview?max_rows=${maxRows}${unmask}${env}`
    )
  },

  getNodeExecutionDetails: (executionId: string, nodeId: string, opts?: { unmask?: boolean; deploymentEnvironment?: string }) => {
    const params = new URLSearchParams()
    if (opts?.unmask) params.set('unmask', 'true')
    if (opts?.deploymentEnvironment) params.set('deployment_environment', opts.deploymentEnvironment)
    const q = params.toString() ? `?${params.toString()}` : ''
    return aiworkflowsRequest<NodeExecutionDetailResponse>(
      `/workflows/executions/${executionId}/nodes/${encodeURIComponent(nodeId)}/details${q}`
    )
  },

  getPmaViewCatalog: (executionId: string, nodeId: string) => {
    return aiworkflowsRequest<PmaViewCatalogResponse>(
      `/workflows/executions/${executionId}/nodes/${encodeURIComponent(nodeId)}/pma`
    )
  },

  getPmaProcessAnalysis: (executionId: string, nodeId: string, index: number) => {
    return aiworkflowsRequest<PmaProcessAnalysisResponse>(
      `/workflows/executions/${executionId}/nodes/${encodeURIComponent(nodeId)}/pma/processes/${index}`
    )
  },

  getPmaProcessBpmn: async (
    executionId: string,
    nodeId: string,
    index: number,
    view: 'as_is' | 'future' = 'as_is'
  ): Promise<string> => {
    const q = new URLSearchParams({ view })
    const baseUrl = config.api.aiworkflowsUrl
    const path = `/workflows/executions/${executionId}/nodes/${encodeURIComponent(nodeId)}/pma/processes/${index}/bpmn?${q}`
    const response = await executeAiworkflowsFetch(`${baseUrl}/api/v1${path}`, {
      headers: { Accept: 'application/xml' },
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new ApiError(
        extractErrorMessage(errorData, `PMA BPMN failed (${response.status})`),
        response.status,
        errorData
      )
    }
    return response.text()
  },

  downloadNodeExecutionPayload: async (
    executionId: string,
    nodeId: string,
    side: 'input' | 'output' = 'output',
    opts?: { unmask?: boolean }
  ): Promise<void> => {
    const q = new URLSearchParams()
    q.set('side', side)
    if (opts?.unmask) q.set('unmask', 'true')
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/workflows/executions/${executionId}/nodes/${encodeURIComponent(nodeId)}/download?${q}`
    const response = await executeAiworkflowsFetch(url, {
      headers: { Accept: 'application/gzip, application/json' },
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new ApiError(
        extractErrorMessage(errorData, `Download failed (${response.status})`),
        response.status,
        errorData
      )
    }
    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition') || ''
    const match = disposition.match(/filename="?([^";]+)"?/)
    const filename = match?.[1] || `node-${nodeId}-${side}.json`
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    a.click()
    URL.revokeObjectURL(objectUrl)
  },

  getNodeDocumentResult: (executionId: string, nodeId: string, opts?: { unmask?: boolean }) => {
    const q = opts?.unmask ? '?unmask=true' : ''
    return aiworkflowsRequest<OcrResultResponse & { _privacy?: PrivacyMeta }>(
      `/workflows/executions/${executionId}/nodes/${encodeURIComponent(nodeId)}/document-result${q}`
    )
  },

  getUnmaskCapability: () =>
    aiworkflowsRequest<{ can_unmask: boolean }>(`/workflows/privacy/unmask-capability`),

  getPrivacyCatalog: () =>
    aiworkflowsRequest<{
      profiles: Record<string, string[]>
      platform_default: WorkflowPrivacyConfig
      workflow_privacy_schema: Record<string, unknown>
    }>(`/workflows/privacy/catalog`),

  getWorkflowEffectivePrivacy: (workflowId: string) =>
    aiworkflowsRequest<{
      enabled: boolean
      profile: string
      entities: string[]
      mask_paths: string[]
      preserve_paths: string[]
      field_annotations: Record<string, string>
      strategy: string
      compliance_mode: string
      workflow_privacy: WorkflowPrivacyConfig | null
    }>(`/workflows/${workflowId}/privacy/effective`),

  cancelExecution: (executionId: string, options?: { deploymentEnvironment?: string }) =>
    aiworkflowsRequest<void>(
      withDeploymentEnvironment(`/workflows/executions/${executionId}/cancel`, options?.deploymentEnvironment),
      { method: 'POST', body: '{}' },
    ),

  pauseExecution: (executionId: string, options?: { deploymentEnvironment?: string }) =>
    aiworkflowsRequest<{ execution_id: string; status: string }>(
      withDeploymentEnvironment(`/workflows/executions/${executionId}/pause`, options?.deploymentEnvironment),
      { method: 'POST', body: '{}' },
    ),

  resumePausedExecution: (executionId: string, options?: { deploymentEnvironment?: string }) =>
    aiworkflowsRequest<{ execution_id: string; status: string }>(
      withDeploymentEnvironment(`/workflows/executions/${executionId}/resume-paused`, options?.deploymentEnvironment),
      { method: 'POST', body: '{}' },
    ),

  getResumePreview: (
    executionId: string,
    params?: {
      strategy?: ResumeStrategy
      target_node_ids?: string
      rerun_dependents?: boolean
      deploymentEnvironment?: string
    }
  ) => {
    const searchParams = new URLSearchParams()
    if (params?.strategy) searchParams.set('strategy', params.strategy)
    if (params?.target_node_ids) searchParams.set('target_node_ids', params.target_node_ids)
    if (params?.rerun_dependents !== undefined) {
      searchParams.set('rerun_dependents', String(params.rerun_dependents))
    }
    if (params?.deploymentEnvironment) {
      searchParams.set('deployment_environment', params.deploymentEnvironment)
    }
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<ResumePreviewResponse>(
      `/workflows/executions/${executionId}/resume-preview${query}`
    )
  },

  resumeExecution: (
    executionId: string,
    body: ResumeExecutionRequest = {},
    options?: { deploymentEnvironment?: string },
  ) =>
    aiworkflowsRequest<ResumeExecutionResponse>(
      withDeploymentEnvironment(`/workflows/executions/${executionId}/resume`, options?.deploymentEnvironment),
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    ),

  // -------------------------------------------------------------------------
  // Node configuration AI (Database Reader / SQL Writer)
  // -------------------------------------------------------------------------

  generateNodeConfigQuery: async (
    tableId: string,
    params: {
      prompt: string
      model_id: string
      project_id?: string
      workflow_id?: string
      node_id?: string
      additional_registered_table_ids?: string[]
    },
    options?: { signal?: AbortSignal }
  ): Promise<
    | { mode: 'structured'; explain: string; querySpec: Record<string, unknown>; estimated_llm_cost_usd?: number | null }
    | { mode: 'raw'; explain: string; rawQuery: string; estimated_llm_cost_usd?: number | null }
  > => {
    const body: Record<string, unknown> = {
      prompt: params.prompt,
      model_id: params.model_id,
    }
    if (params.project_id) body.project_id = params.project_id
    if (params.workflow_id) body.workflow_id = params.workflow_id
    if (params.node_id) body.node_id = params.node_id
    if (params.additional_registered_table_ids?.length) {
      body.additional_registered_table_ids = params.additional_registered_table_ids
    }
    const raw = await aiworkflowsRequest<Record<string, unknown>>(
      `/workflows/node-config-ai/registered-tables/${tableId}/generate-query`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal: options?.signal,
      }
    )
    const mode = raw.mode as string
    const explain = String(raw.explain ?? '')
    const querySpec = (raw.querySpec ?? raw.query_spec) as Record<string, unknown> | undefined
    const rawQuery = (raw.rawQuery ?? raw.raw_query) as string | undefined
    const estimatedLlmCostUsd =
      typeof raw.estimated_llm_cost_usd === 'number' ? raw.estimated_llm_cost_usd : null
    if (mode === 'raw' && rawQuery) {
      return { mode: 'raw', explain, rawQuery, estimated_llm_cost_usd: estimatedLlmCostUsd }
    }
    if (mode === 'structured' && querySpec && typeof querySpec === 'object') {
      return { mode: 'structured', explain, querySpec, estimated_llm_cost_usd: estimatedLlmCostUsd }
    }
    throw new ApiError('Unexpected generate-query response from server', 500, raw)
  },

  generateNodeConfigDdl: async (
    connectionId: string,
    params: {
      prompt: string
      source_schema: string
      model_id: string
      project_id?: string
      workflow_id?: string
      node_id?: string
      schema_name?: string
      table_name?: string
      upstream_columns?: string[]
      upsert_key_columns?: string[]
    },
    options?: { signal?: AbortSignal }
  ): Promise<{ explain: string; ddl: string }> => {
    const body: Record<string, unknown> = {
      prompt: params.prompt,
      source_schema: params.source_schema,
      model_id: params.model_id,
    }
    if (params.project_id) body.project_id = params.project_id
    if (params.workflow_id) body.workflow_id = params.workflow_id
    if (params.node_id) body.node_id = params.node_id
    if (params.schema_name) body.schema_name = params.schema_name
    if (params.table_name) body.table_name = params.table_name
    if (params.upstream_columns?.length) body.upstream_columns = params.upstream_columns
    if (params.upsert_key_columns?.length) body.upsert_key_columns = params.upsert_key_columns
    const raw = await aiworkflowsRequest<Record<string, unknown>>(
      `/workflows/node-config-ai/connectors/${connectionId}/generate-ddl`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal: options?.signal,
      }
    )
    const explain = String(raw.explain ?? '')
    const ddl = String(raw.ddl ?? '')
    if (!ddl.trim()) {
      throw new ApiError('Unexpected generate-ddl response: empty ddl', 500, raw)
    }
    return { explain, ddl }
  },

  generateOcrExtractionSchema: async (
    params: {
      prompt?: string
      model_id: string
      sample_document_base64?: string
      sample_mime_type?: string
      project_id?: string
      workflow_id?: string
      node_id?: string
    },
    options?: { signal?: AbortSignal }
  ): Promise<{ schema: Record<string, unknown> }> => {
    const body: Record<string, unknown> = {
      model_id: params.model_id,
    }
    if (params.prompt) body.prompt = params.prompt
    if (params.sample_document_base64) body.sample_document_base64 = params.sample_document_base64
    if (params.sample_mime_type) body.sample_mime_type = params.sample_mime_type
    if (params.project_id) body.project_id = params.project_id
    if (params.workflow_id) body.workflow_id = params.workflow_id
    if (params.node_id) body.node_id = params.node_id
    const raw = await aiworkflowsRequest<Record<string, unknown>>(
      '/workflows/node-config-ai/ocr/generate-extraction-schema',
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal: options?.signal,
      }
    )
    const schema = raw.schema
    if (!schema || typeof schema !== 'object' || Array.isArray(schema)) {
      throw new ApiError('Unexpected generate-extraction-schema response: missing schema', 500, raw)
    }
    return { schema: schema as Record<string, unknown> }
  },

  validateNodeConfigWriterDdl: async (
    connectionId: string,
    params: {
      ddl: string
      schema_name?: string
      table_name?: string
      upstream_columns?: string[]
      upsert_key_columns?: string[]
    },
    options?: { signal?: AbortSignal }
  ): Promise<{ valid: boolean; ddl: string; errors: string[] }> => {
    const body: Record<string, unknown> = { ddl: params.ddl }
    if (params.schema_name) body.schema_name = params.schema_name
    if (params.table_name) body.table_name = params.table_name
    if (params.upstream_columns?.length) body.upstream_columns = params.upstream_columns
    if (params.upsert_key_columns?.length) body.upsert_key_columns = params.upsert_key_columns
    const raw = await aiworkflowsRequest<Record<string, unknown>>(
      `/workflows/node-config-ai/connectors/${connectionId}/validate-writer-ddl`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal: options?.signal,
      }
    )
    return {
      valid: Boolean(raw.valid),
      ddl: String(raw.ddl ?? ''),
      errors: Array.isArray(raw.errors) ? raw.errors.map((e) => String(e)) : [],
    }
  },

  // -------------------------------------------------------------------------
  // Metrics (non-paginated, designed for dashboard charts)
  // -------------------------------------------------------------------------

  getWorkflowPreBuildCostMetrics: (
    workflowId: string,
    params?: { period?: string; start_date?: string; end_date?: string }
  ) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<PreBuildCostMetricsResponse>(
      `/workflows/${workflowId}/metrics/prebuild-cost${query}`
    )
  },

  getProjectPreBuildCostMetrics: (
    projectId: string,
    params?: { period?: string; start_date?: string; end_date?: string }
  ) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<PreBuildCostMetricsResponse>(
      `/projects/${projectId}/metrics/prebuild-cost${query}`
    )
  },

  getTenantPreBuildCostMetrics: (params?: { period?: string; start_date?: string; end_date?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<PreBuildCostMetricsResponse>(
      `/tenant/metrics/prebuild-cost${query}`
    )
  },

  getWorkflowMetricsAggregate: (
    workflowId: string,
    params?: { period?: string; start_date?: string; end_date?: string }
  ) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<MetricsAggregateResponse>(
      `/workflows/${workflowId}/metrics/aggregate${query}`
    )
  },

  getProjectMetricsAggregate: (
    projectId: string,
    params?: { period?: string; start_date?: string; end_date?: string }
  ) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<MetricsAggregateResponse>(
      `/projects/${projectId}/metrics/aggregate${query}`
    )
  },

  getTenantMetricsAggregate: (params?: { period?: string; start_date?: string; end_date?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<MetricsAggregateResponse>(
      `/tenant/metrics/aggregate${query}`
    )
  },

  getExecutionMetrics: (executionId: string) =>
    aiworkflowsRequest<ExecutionMetricsDetailResponse>(
      `/workflows/executions/${executionId}/metrics`
    ),

  exportMetricsCSV: async (
    workflowId: string,
    params?: { period?: string; start_date?: string; end_date?: string; include_nodes?: boolean }
  ): Promise<Blob> => {
    const baseUrl = config.api.aiworkflowsUrl
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.include_nodes) searchParams.set('include_nodes', 'true')
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    const url = `${baseUrl}/api/v1/workflows/${workflowId}/metrics/export${query}`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const headers: Record<string, string> = {
      Accept: 'text/csv',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    return fetchWorkflowMetricsExportCsvBlob(url, headers)
  },

  exportProjectMetricsCSV: async (
    projectId: string,
    params?: { period?: string; start_date?: string; end_date?: string; include_nodes?: boolean }
  ): Promise<Blob> => {
    const baseUrl = config.api.aiworkflowsUrl
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.include_nodes) searchParams.set('include_nodes', 'true')
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    const url = `${baseUrl}/api/v1/projects/${projectId}/metrics/export${query}`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const headers: Record<string, string> = {
      Accept: 'text/csv',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    return fetchWorkflowMetricsExportCsvBlob(url, headers)
  },

  exportTenantMetricsCSV: async (
    params?: { period?: string; start_date?: string; end_date?: string; include_nodes?: boolean }
  ): Promise<Blob> => {
    const baseUrl = config.api.aiworkflowsUrl
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.include_nodes) searchParams.set('include_nodes', 'true')
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    const url = `${baseUrl}/api/v1/tenant/metrics/export${query}`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const headers: Record<string, string> = {
      Accept: 'text/csv',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    return fetchWorkflowMetricsExportCsvBlob(url, headers)
  },

  // -------------------------------------------------------------------------
  // Schedules
  // -------------------------------------------------------------------------

  createSchedule: (workflowId: string, data: ScheduleCreatePayload) => {
    let refreshToken: string | null = null
    try {
      refreshToken = localStorage.getItem('tagp-refresh-token')
    } catch {
      /* continue without */
    }
    return aiworkflowsRequest<ScheduleReadResponse>(`/workflows/${workflowId}/schedule`, {
      method: 'POST',
      headers: { ...(refreshToken && { 'X-Refresh-Token': refreshToken }) },
      body: JSON.stringify(data),
    })
  },

  getSchedule: (workflowId: string) =>
    aiworkflowsRequest<ScheduleReadResponse>(`/workflows/${workflowId}/schedule`),

  updateSchedule: (workflowId: string, data: Partial<ScheduleCreatePayload>) => {
    let refreshToken: string | null = null
    try {
      refreshToken = localStorage.getItem('tagp-refresh-token')
    } catch {
      /* continue without */
    }
    return aiworkflowsRequest<ScheduleReadResponse>(`/workflows/${workflowId}/schedule`, {
      method: 'PUT',
      headers: { ...(refreshToken && { 'X-Refresh-Token': refreshToken }) },
      body: JSON.stringify(data),
    })
  },

  deleteSchedule: (workflowId: string) =>
    aiworkflowsRequest<void>(`/workflows/${workflowId}/schedule`, {
      method: 'DELETE',
    }),

  pauseSchedule: (workflowId: string, versionId?: string) => {
    const query = versionId ? `?version_id=${encodeURIComponent(versionId)}` : ''
    return aiworkflowsRequest<ScheduleReadResponse>(`/workflows/${workflowId}/schedule/pause${query}`, {
      method: 'PUT',
      body: '{}',
    })
  },

  resumeSchedule: (workflowId: string, versionId?: string) => {
    const query = versionId ? `?version_id=${encodeURIComponent(versionId)}` : ''
    return aiworkflowsRequest<ScheduleReadResponse>(`/workflows/${workflowId}/schedule/resume${query}`, {
      method: 'PUT',
      body: '{}',
    })
  },

  getDeploymentSchedule: (workflowId: string, deploymentId: string) =>
    aiworkflowsRequest<ScheduleReadResponse | null>(
      `/workflows/${workflowId}/deployments/${deploymentId}/schedule`
    ),

  upsertDeploymentSchedule: (workflowId: string, deploymentId: string, data: ScheduleCreatePayload) => {
    let refreshToken: string | null = null
    try {
      refreshToken = localStorage.getItem('tagp-refresh-token')
    } catch {
      /* continue without */
    }
    return aiworkflowsRequest<ScheduleReadResponse>(
      `/workflows/${workflowId}/deployments/${deploymentId}/schedule`,
      {
        method: 'PUT',
        headers: { ...(refreshToken && { 'X-Refresh-Token': refreshToken }) },
        body: JSON.stringify(data),
      }
    )
  },

  deleteDeploymentSchedule: (workflowId: string, deploymentId: string) =>
    aiworkflowsRequest<void>(
      `/workflows/${workflowId}/deployments/${deploymentId}/schedule`,
      { method: 'DELETE' }
    ),

  pauseDeploymentSchedule: (workflowId: string, deploymentId: string) =>
    aiworkflowsRequest<ScheduleReadResponse>(
      `/workflows/${workflowId}/deployments/${deploymentId}/schedule/pause`,
      { method: 'POST', body: '{}' }
    ),

  resumeDeploymentSchedule: (workflowId: string, deploymentId: string) =>
    aiworkflowsRequest<ScheduleReadResponse>(
      `/workflows/${workflowId}/deployments/${deploymentId}/schedule/resume`,
      { method: 'POST', body: '{}' }
    ),

  // -------------------------------------------------------------------------
  // Workflow Controls (unified pause/resume for triggers + schedule)
  // -------------------------------------------------------------------------

  getWorkflowControls: (workflowId: string, deploymentId?: string) => {
    const query = deploymentId
      ? `?deployment_id=${encodeURIComponent(deploymentId)}`
      : ''
    return aiworkflowsRequest<WorkflowControlsRead>(
      `/workflows/${workflowId}/controls${query}`
    )
  },

  updateWorkflowControlStatus: (
    workflowId: string,
    itemId: string,
    data: WorkflowControlStatusUpdatePayload,
    deploymentId?: string
  ) => {
    const query = deploymentId
      ? `?deployment_id=${encodeURIComponent(deploymentId)}`
      : ''
    return aiworkflowsRequest<WorkflowControlsRead>(
      `/workflows/${workflowId}/controls/${itemId}${query}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
  },

  pauseAllWorkflowControls: (workflowId: string, deploymentId?: string) => {
    const query = deploymentId
      ? `?deployment_id=${encodeURIComponent(deploymentId)}`
      : ''
    return aiworkflowsRequest<WorkflowControlsBulkResult>(
      `/workflows/${workflowId}/controls/pause-all${query}`,
      {
        method: 'PUT',
        body: '{}',
      }
    )
  },

  resumeAllWorkflowControls: (workflowId: string, deploymentId?: string) => {
    const query = deploymentId
      ? `?deployment_id=${encodeURIComponent(deploymentId)}`
      : ''
    return aiworkflowsRequest<WorkflowControlsBulkResult>(
      `/workflows/${workflowId}/controls/resume-all${query}`,
      {
        method: 'PUT',
        body: '{}',
      }
    )
  },

  // -------------------------------------------------------------------------
  // Triggers (Webhook + Storage)
  // -------------------------------------------------------------------------

  listTriggers: (workflowId: string) =>
    aiworkflowsRequest<{ items: TriggerRead[] }>(`/workflows/${workflowId}/triggers`),

  upsertTrigger: (workflowId: string, data: TriggerUpsertPayload) => {
    let refreshToken: string | null = null
    try {
      refreshToken = localStorage.getItem('tagp-refresh-token')
    } catch {
      /* continue without */
    }
    return aiworkflowsRequest<TriggerRead>(`/workflows/${workflowId}/triggers`, {
      method: 'POST',
      headers: { ...(refreshToken && { 'X-Refresh-Token': refreshToken }) },
      body: JSON.stringify(data),
    })
  },

  updateTriggerStatus: (
    workflowId: string,
    triggerId: string,
    data: TriggerStatusUpdatePayload
  ) =>
    aiworkflowsRequest<TriggerRead>(`/workflows/${workflowId}/triggers/${triggerId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  listTriggerTokens: (workflowId: string, triggerId: string) =>
    aiworkflowsRequest<{ items: TriggerTokenRead[] }>(
      `/workflows/${workflowId}/triggers/${triggerId}/tokens`
    ),

  createTriggerToken: (workflowId: string, triggerId: string, data?: TriggerTokenCreatePayload) => {
    let refreshToken: string | null = null
    try {
      refreshToken = localStorage.getItem('tagp-refresh-token')
    } catch {
      /* continue without */
    }
    return aiworkflowsRequest<TriggerTokenCreateResponse>(
      `/workflows/${workflowId}/triggers/${triggerId}/tokens`,
      {
        method: 'POST',
        headers: { ...(refreshToken && { 'X-Refresh-Token': refreshToken }) },
        body: data ? JSON.stringify(data) : '{}',
      }
    )
  },

  updateTriggerToken: (
    workflowId: string,
    triggerId: string,
    tokenId: string,
    data: TriggerTokenUpdatePayload
  ) =>
    aiworkflowsRequest<TriggerTokenRead>(
      `/workflows/${workflowId}/triggers/${triggerId}/tokens/${tokenId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  rebindTriggerTokenRunAsCredentials: (workflowId: string, triggerId: string, tokenId: string) => {
    let refreshToken: string | null = null
    try {
      refreshToken = localStorage.getItem('tagp-refresh-token')
    } catch {
      /* continue without */
    }
    return aiworkflowsRequest<TriggerTokenRead>(
      `/workflows/${workflowId}/triggers/${triggerId}/tokens/${tokenId}/run-as-credentials`,
      {
        method: 'POST',
        headers: { ...(refreshToken && { 'X-Refresh-Token': refreshToken }) },
        body: '{}',
      }
    )
  },

  revokeTriggerToken: (workflowId: string, triggerId: string, tokenId: string) =>
    aiworkflowsRequest<{ revoked: boolean; token_id: string }>(
      `/workflows/${workflowId}/triggers/${triggerId}/tokens/${tokenId}`,
      { method: 'DELETE' }
    ),

  testTrigger: (workflowId: string, triggerId: string, data?: TriggerTestPayload) =>
    aiworkflowsRequest<TriggerTestResponse>(
      `/workflows/${workflowId}/triggers/${triggerId}/test`,
      {
        method: 'POST',
        body: data ? JSON.stringify(data) : '{}',
      }
    ),

  // -------------------------------------------------------------------------
  // Node Registry
  // -------------------------------------------------------------------------

  getNodeRegistry: () =>
    aiworkflowsRequest<NodeRegistryItem[]>('/workflows/nodes/registry'),

  // -------------------------------------------------------------------------
  // User Tool Credentials
  // -------------------------------------------------------------------------

  listUserToolCredentials: (params?: { tool_type?: string; project_id?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.tool_type) searchParams.set('tool_type', params.tool_type)
    if (params?.project_id) searchParams.set('project_id', params.project_id)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<UserToolCredentialList>(`/user-tool-credentials${query}`)
  },

  createUserToolCredential: (data: UserToolCredentialCreatePayload) =>
    aiworkflowsRequest<UserToolCredential>('/user-tool-credentials', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateUserToolCredential: (credentialId: string, data: UserToolCredentialUpdatePayload) =>
    aiworkflowsRequest<UserToolCredential>(`/user-tool-credentials/${credentialId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteUserToolCredential: (credentialId: string) =>
    aiworkflowsRequest<void>(`/user-tool-credentials/${credentialId}`, {
      method: 'DELETE',
    }),

  // -------------------------------------------------------------------------
  // Export / Import
  // -------------------------------------------------------------------------

  /** Get workflow dependencies (agents, prompts, skills, models, etc.) for export modal */
  getWorkflowDependencies: (workflowId: string) =>
    aiworkflowsRequest<WorkflowDependenciesResponse>(
      `/workflows/export-import/${workflowId}/dependencies`
    ),

  exportWorkflowPackage: async (
    workflowId: string,
    options: WorkflowPackageExportOptions = {}
  ): Promise<{ blob: Blob; filename: string; entityCount: number; warnings: string[] }> => {
    const baseUrl = config.api.aiworkflowsUrl
    const params = new URLSearchParams()
    if (options.include_agents !== undefined) {
      params.set('include_agents', String(options.include_agents))
    }
    if (options.include_agent_dependencies !== undefined) {
      params.set('include_agent_dependencies', String(options.include_agent_dependencies))
    }
    if (options.include_workflow_deps !== undefined) {
      params.set('include_workflow_deps', String(options.include_workflow_deps))
    }
    if (options.include_models !== undefined) {
      params.set('include_models', String(options.include_models))
    }
    if (options.include_connectors !== undefined) {
      params.set('include_connectors', String(options.include_connectors))
    }
    if (options.include_iam !== undefined) {
      params.set('include_iam', String(options.include_iam))
    }
    const qs = params.toString()
    const querySuffix = qs ? `?${qs}` : ''
    const url = `${baseUrl}/api/v1/workflows/export-import/${workflowId}/export-package${querySuffix}`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const headers: Record<string, string> = {
      Accept: 'application/zip',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    const response = await aiworkflowsFetch(url, { headers })
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ detail: 'Export failed' }))
      const detail = errorBody?.detail
      const message =
        typeof detail === 'string'
          ? detail
          : detail?.message || `Export failed with status ${response.status}`
      throw new ApiError(message, response.status)
    }
    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition') || ''
    const match = disposition.match(/filename="?([^";]+)"?/)
    const filename = match?.[1] || 'workflow-package.zip'
    const entityCount = Number(response.headers.get('X-Package-Entity-Count') || '0')
    const warnings = readPackageExportWarnings(response.headers.get('X-Package-Warnings'))
    return { blob, filename, entityCount, warnings }
  },

  exportWorkflow: async (workflowId: string): Promise<string> => {
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/workflows/export-import/${workflowId}/export?format=yaml`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const headers: Record<string, string> = {
      Accept: 'text/yaml',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    const response = await aiworkflowsFetch(url, { headers })
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Export failed')
      throw new ApiError(errorText || `Export failed with status ${response.status}`, response.status)
    }
    return response.text()
  },

  /** PMA-gated: whether BPMN download is available for this saved workflow. */
  bpmnExportAvailable: async (
    workflowId: string,
  ): Promise<{ available: boolean; reason?: string; process_name?: string }> => {
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/workflows/export-import/${workflowId}/bpmn/available`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    const response = await aiworkflowsFetch(url, { headers })
    if (!response.ok) {
      if (response.status === 404) {
        return { available: false, reason: 'not_found' }
      }
      const errorText = await response.text().catch(() => 'BPMN availability check failed')
      throw new ApiError(
        errorText || `BPMN availability failed with status ${response.status}`,
        response.status,
      )
    }
    return response.json()
  },

  /** PMA-gated: download plain BPMN 2.0 XML for a saved workflow. */
  exportWorkflowBpmn: async (
    workflowId: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/workflows/export-import/${workflowId}/bpmn`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const headers: Record<string, string> = {
      Accept: 'application/xml',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    const response = await aiworkflowsFetch(url, { headers })
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ detail: 'BPMN export failed' }))
      const detail = errorBody?.detail
      const message =
        typeof detail === 'string'
          ? detail
          : detail?.message || `BPMN export failed with status ${response.status}`
      throw new ApiError(message, response.status)
    }
    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition') || ''
    const match = disposition.match(/filename="?([^";]+)"?/)
    const filename = match?.[1] || 'workflow.bpmn'
    return { blob, filename }
  },

  bulkExportWorkflows: async (
    workflowIds: string[],
    includeDependencies = false,
  ): Promise<string> => {
    const baseUrl = config.api.aiworkflowsUrl
    const depsParam = includeDependencies ? '&include_dependencies=true' : ''
    const url = `${baseUrl}/api/v1/workflows/export-import/bulk-export?format=yaml${depsParam}`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/yaml',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    const response = await aiworkflowsFetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(workflowIds),
    })
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Export failed')
      throw new ApiError(errorText || `Export failed with status ${response.status}`, response.status)
    }
    return response.text()
  },

  importWorkflowFile: async (
    file: File,
    projectId: string,
    dryRun = false
  ): Promise<WorkflowImportResult> => {
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/workflows/export-import/import`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('project_id', projectId)
    formData.append('dry_run', String(dryRun))

    const headers: Record<string, string> = {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    const response = await aiworkflowsFetch(url, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Import failed' }))
      throw new ApiError(error.detail || 'Import failed', response.status)
    }
    return response.json()
  },

  /**
   * Import a workflow package ZIP. Dependencies (prompts, skills, agents, ...)
   * are imported first in manifest order, then the workflow itself, so agent
   * references hydrate to the newly-created entities instead of placeholders.
   */
  importWorkflowPackage: async (
    file: File,
    projectId: string,
    options: WorkflowPackageImportOptions = {}
  ): Promise<WorkflowPackageImportResult> => {
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/workflows/export-import/import-package`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('project_id', projectId)
    formData.append('dry_run', String(options.dryRun ?? false))
    formData.append('overwrite', String(options.overwrite ?? false))

    const headers: Record<string, string> = {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    const response = await aiworkflowsFetch(url, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Package import failed' }))
      throw new ApiError(error.detail || 'Package import failed', response.status)
    }
    return response.json()
  },

  importWorkflowYaml: async (
    yamlContent: string,
    projectId: string,
    dryRun = false
  ): Promise<WorkflowImportResult> => {
    return aiworkflowsRequest<WorkflowImportResult>('/workflows/export-import/import/yaml', {
      method: 'POST',
      body: JSON.stringify({
        yaml_content: yamlContent,
        project_id: projectId,
        dry_run: dryRun,
      }),
    })
  },

  importN8nWorkflow: async (payload: N8nImportRequest): Promise<N8nImportResponse> => {
    return aiworkflowsRequest<N8nImportResponse>('/workflows/import/n8n', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  // -------------------------------------------------------------------------
  // Workflow Templates (Solution Templates)
  // -------------------------------------------------------------------------

  listTemplates: (params?: {
    include_archived?: boolean
    search?: string
    offset?: number
    limit?: number
    created_by?: string | string[]
  }) => {
    const searchParams = new URLSearchParams()
    const appendAll = (key: string, value?: string | string[]) => {
      if (value === undefined) return
      for (const entry of Array.isArray(value) ? value : [value]) {
        if (entry) searchParams.append(key, entry)
      }
    }
    if (params?.include_archived !== undefined)
      searchParams.set('include_archived', String(params.include_archived))
    if (params?.search) searchParams.set('search', params.search)
    if (params?.offset !== undefined) searchParams.set('offset', String(params.offset))
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit))
    appendAll('created_by', params?.created_by)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<WorkflowTemplateListResponse>(`/templates${query}`)
  },

  getTemplate: (templateId: string) =>
    aiworkflowsRequest<WorkflowTemplateRead>(`/templates/${templateId}`),

  checkTemplateNameUniqueness: (name: string, excludeId?: string) => {
    const params = new URLSearchParams()
    params.append('name', name)
    if (excludeId) params.append('exclude_id', excludeId)
    return aiworkflowsRequest<{ is_unique: boolean; message: string | null }>(
      `/templates/check-name?${params.toString()}`
    )
  },

  publishTemplate: (workflowId: string, data: WorkflowTemplateCreatePayload) => {
    const searchParams = new URLSearchParams()
    searchParams.set('workflow_id', workflowId)
    return aiworkflowsRequest<WorkflowTemplateRead>(
      `/templates/publish?${searchParams.toString()}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  archiveTemplate: (templateId: string) =>
    aiworkflowsRequest<WorkflowTemplateListItem>(`/templates/${templateId}/archive`, {
      method: 'PATCH',
      // Empty JSON body so browsers send Content-Length; Akamai/CDN often returns 411 without it.
      body: '{}',
    }),

  unarchiveTemplate: (templateId: string) =>
    aiworkflowsRequest<WorkflowTemplateListItem>(`/templates/${templateId}/unarchive`, {
      method: 'PATCH',
      body: '{}',
    }),

  // -------------------------------------------------------------------------
  // Quick-Fix (AI Error Analysis)
  // -------------------------------------------------------------------------

  analyzeError: (data: QuickFixRequest) =>
    aiworkflowsRequest<QuickFixResponse>('/workflows/quickfix/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // -------------------------------------------------------------------------
  // OCR DPI autosuggestion
  // -------------------------------------------------------------------------

  /**
   * Analyze a sample PDF/image (or platform file_id) and return a suggested
   * ``pdf_render_dpi`` for the OCR node (clamped 72–600).
   */
  analyzeDpi: async (opts: {
    file?: File
    fileId?: string
  }): Promise<DpiAnalysisResponse> => {
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/workflows/analyze-dpi`

    let token: string | null = null
    try { token = localStorage.getItem(authConfig.tokenKey) } catch { /* ignore */ }

    let tenantId: string | null = null
    try { tenantId = localStorage.getItem('tagp-tenant-id') } catch { /* ignore */ }

    const formData = new FormData()
    if (opts.file) {
      formData.append('file', opts.file)
    }
    if (opts.fileId) {
      formData.append('file_id', opts.fileId)
    }

    const headers: Record<string, string> = {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    }

    const response = await aiworkflowsFetch(url, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'DPI analysis failed' }))
      throw new ApiError(
        extractErrorMessage(error, 'DPI analysis failed'),
        response.status,
        error,
      )
    }
    return response.json()
  },

  // -------------------------------------------------------------------------
  // Node Suggestions
  // -------------------------------------------------------------------------

  getNextNodeSuggestions: (data: NextNodeSuggestionRequest) =>
    aiworkflowsRequest<NextNodeSuggestionResponse>(
      '/workflows/suggestions/next-nodes',
      { method: 'POST', body: JSON.stringify(data) },
    ),

  // -------------------------------------------------------------------------
  // OpenAPI (Docs)
  // -------------------------------------------------------------------------

  getOpenApiJson: () => aiworkflowsRootRequest<OpenAPISpec>('/openapi.json'),

  /** Node types that have curated per-field documentation in the backend catalog. */
  listWorkflowNodeConfigReferenceIndex: () =>
    aiworkflowsRequest<WorkflowNodeConfigReferenceIndexResponse>('/workflow-nodes/config-reference'),

  /** Field-level configuration reference for one workflow node type (markdown descriptions). */
  getWorkflowNodeConfigReference: (nodeType: string) =>
    aiworkflowsRequest<WorkflowNodeConfigReferenceResponse>(
      `/workflow-nodes/${encodeURIComponent(nodeType)}/config-reference`,
    ),

  /** Bulk input/output schemas and config parameters for all node types. */
  getWorkflowNodeSchemas: () =>
    aiworkflowsRequest<AllNodeSchemasResponse>('/workflow-nodes/schemas'),

  /** Jurisdiction-agnostic OCR analyzer template catalog. */
  getOcrAnalyzerTemplates: () =>
    aiworkflowsRequest<OcrAnalyzerTemplatesResponse>('/workflow-nodes/ocr/analyzer-templates'),

  /** Schema + optional AI validation for a single workflow node config. */
  validateWorkflowNodeConfig: (body: WorkflowNodeConfigValidateRequest) =>
    aiworkflowsRequest<WorkflowNodeConfigValidateResponse>('/workflow-nodes/validate-config', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Graph-level health: dead router branches, unreachable nodes, dead ends. */
  getWorkflowGraphHealth: (body: WorkflowGraphHealthRequest) =>
    aiworkflowsRequest<WorkflowGraphHealthResponse>('/workflow-nodes/graph-health', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  searchOpenApiOperations: (query: string) => {
    const q = new URLSearchParams({ query })
    return aiworkflowsRequest<OpenApiSearchResponse>(`/openapi/search?${q.toString()}`)
  },

  // -------------------------------------------------------------------------
  // Admin — General settings · AI Workflows AI Build (per-stage models)
  // -------------------------------------------------------------------------

  getAiBuildLlmSettings: () =>
    aiworkflowsRequest<CwbAiBuildLlmSettingsRead>('/admin/ai-workflows/ai-build-llm'),

  putAiBuildLlmSettings: (body: CwbAiBuildLlmSettingsUpdate) =>
    aiworkflowsRequest<CwbAiBuildLlmSettingsRead>('/admin/ai-workflows/ai-build-llm', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteAiBuildLlmSettings: () =>
    aiworkflowsRequest<void>('/admin/ai-workflows/ai-build-llm', {
      method: 'DELETE',
    }),

  getTenantPrivacySettings: () =>
    aiworkflowsRequest<TenantPrivacySettingsRead>('/admin/ai-workflows/privacy'),

  putTenantPrivacySettings: (body: TenantPrivacySettingsUpdate) =>
    aiworkflowsRequest<TenantPrivacySettingsRead>('/admin/ai-workflows/privacy', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteTenantPrivacySettings: () =>
    aiworkflowsRequest<void>('/admin/ai-workflows/privacy', {
      method: 'DELETE',
    }),

  // -------------------------------------------------------------------------
  // Blueprint library — prompt-driven spec (model: Admin → Workflow AI → Blueprint creation)
  // -------------------------------------------------------------------------

  generateBlueprintSpec: (body: {
    industry: string
    apqc_processes: Array<Record<string, unknown>>
    user_prompt: string
    existing_spec?: Record<string, unknown> | null
  }) =>
    aiworkflowsRequest<BlueprintSpecGenerateResponse>('/blueprint-spec/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * SSE streaming variant — emits partial results as each generation phase completes.
   * Calls `onPhaseA` with the partial spec after structuring+optimization, then
   * `onComplete` with the full spec after agentification+validation.
   */
  generateBlueprintSpecStream: async (
    body: {
      industry: string
      apqc_processes: Array<Record<string, unknown>>
      user_prompt: string
      existing_spec?: Record<string, unknown> | null
    },
    callbacks: {
      onPhaseA?: (data: BlueprintSpecStreamEvent) => void
      onComplete?: (data: BlueprintSpecStreamEvent) => void
      onError?: (data: BlueprintSpecStreamEvent) => void
    },
    options?: { signal?: AbortSignal },
  ): Promise<BlueprintSpecGenerateResponse> => {
    const baseUrl = config.api.aiworkflowsUrl
    const url = `${baseUrl}/api/v1/blueprint-spec/generate-stream`
    const headers = getAiworkflowsAuthHeaders()

    const response = await aiworkflowsFetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: options?.signal,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = extractErrorMessage(errorData, `Request failed with status ${response.status}`)
      throw new ApiError(errorMessage, response.status, errorData)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''
    let finalResult: BlueprintSpecGenerateResponse | null = null
    let streamErrorMessage: string | null = null

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      let currentEvent = ''
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim()
        } else if (line.startsWith('data: ')) {
          const dataStr = line.slice(6).trim()
          if (!dataStr || dataStr === '{}') {
            currentEvent = ''
            continue
          }
          try {
            const parsed = JSON.parse(dataStr) as BlueprintSpecStreamEvent & { message?: string }
            if (currentEvent === 'phase_a' || parsed.event === 'phase_a') {
              callbacks.onPhaseA?.(parsed)
            } else if (currentEvent === 'complete' || parsed.event === 'complete') {
              callbacks.onComplete?.(parsed)
              finalResult = {
                spec: parsed.spec ?? {},
                stages_completed: parsed.stages_completed,
                tokens_input: parsed.tokens_input,
                tokens_output: parsed.tokens_output,
                foundation_model_id: parsed.foundation_model_id,
                model_id: parsed.model_id,
                estimated_cost_usd: parsed.estimated_cost_usd,
              }
            } else if (currentEvent === 'error' || parsed.event === 'error') {
              const msg = resolveBlueprintSpecStreamErrorMessage(parsed.message)
              streamErrorMessage = msg
              callbacks.onError?.(parsed)
            }
          } catch {
            // skip malformed JSON
          }
          currentEvent = ''
        }
      }
    }

    if (streamErrorMessage) {
      throw new ApiError(streamErrorMessage, 500, { detail: streamErrorMessage })
    }

    return finalResult ?? { spec: {} }
  },

  // -------------------------------------------------------------------------
  // CWB (AI Workflow Builder) Metrics
  // -------------------------------------------------------------------------

  getCWBProjectMetrics: (
    projectId: string,
    params?: { period?: string; start_date?: string; end_date?: string }
  ) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<CWBMetricsAggregateResponse>(
      `/cwb/sessions/metrics/project/${projectId}${query}`
    )
  },

  getCWBTenantMetrics: (params?: { period?: string; start_date?: string; end_date?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return aiworkflowsRequest<CWBMetricsAggregateResponse>(
      `/cwb/sessions/metrics/tenant${query}`
    )
  },
}
